// src/components/CommonMap/CommonMap.jsx
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import Map, { Source, Layer, NavigationControl, Popup } from 'react-map-gl';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import HomeIcon from '@mui/icons-material/Home';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LayersIcon from '@mui/icons-material/Layers';
import { styled } from '@mui/system';
import axios from 'axios';
import * as turf from '@turf/turf';
import { clipFeaturesToPolygons } from '../../utils/loteSpatialFilter';

import BarIndicator from '../../components/BarIndicator/BarIndicator';
import MapDialog from '../../components/MapDialog/MapDialog';

const VAR_DETAIL_ZOOM_THRESHOLD = 16;

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    marginTop: theme.spacing(1),
    backgroundColor: 'white',
    color: 'black',
    '&:hover': {
        backgroundColor: 'lightgray',
    },
    width: 36,
    right: 50,
    height: 36,
}));

const CommonMap = (props) => {
    const {
        center = [14.634915, -90.506882],
        zoom = 8,
        polygons = [],
        lines = [],
        points = [],
        areasSuperpuestas = [],
        nonIntersectedAreas = [],
        bufferedLines = [],
        bufferedIntersections = [],
        onLineHover = () => {},
        onLineMouseOut = () => {},
        onLineClick = () => {},
        polygonProperties = [],
        showIntersections = true,
        mapRef,
        polygonsData = [],
        visibleLotePolygons,
        highlightedLote = null,
        activeLotes = [],
        onSelectLote = () => {},
        onHoverLote = () => {},
        onLeaveLote = () => {},
        imageUrl = '',
        northWestCoords,
        southEastCoords,
        filteredColors,
        selectedLine,
        getLineColor,
        lineasNoFiltradas = [],
        popupInfo,
        setPopupInfo,
        selectedFiles,
        isEditing,
        tramplingLines,
        polygonsHarvestPlanning,
        polygonsHerbicidesPlanning,
        activeTool,
        isFilterDialogOpen,
        closeFilterDialog,
        setLabelsAndColors,
        setTipoMapeo,
        setNorthWestCoords,
        setSouthEastCoords,
        setCoordinateSystem,
        clipPolygonsFC,
        onClippedBufferChange = () => {},
        onClipPolygonsChange = () => {},
        isTasaVariableMap = false,
        tasaVariableRemoteLayers = null,
        tasaVariableBounds = null,
    } = props;

    const localMapRef = useRef(null);

    const normalizedTasaVariableRemoteLayers = useMemo(() => {
        const layers = tasaVariableRemoteLayers && typeof tasaVariableRemoteLayers === 'object'
            ? tasaVariableRemoteLayers
            : {};
        return {
            // Herbicidas: la aplicación llega como polígonos de franja.
            geometriaAplicacion: layers.geometriaAplicacion || layers.geometria_aplicacion || null,
            aplicacionPoligonos: layers.aplicacionPoligonos || layers.aplicacion_poligonos || null,
            aplicacionPoligonosOverview: layers.aplicacionPoligonosOverview || layers.aplicacion_poligonos_overview || null,
            lineasAplicacion: layers.lineasAplicacion || layers.lineas_aplicacion || null,
            lineasAplicacionOverview: layers.lineasAplicacionOverview || layers.lineas_aplicacion_overview || null,
            lineasAplicadasEnMaleza: layers.lineasAplicadasEnMaleza || layers.lineas_aplicadas_en_maleza || null,
            lineasAplicadasFueraMaleza: layers.lineasAplicadasFueraMaleza || layers.lineas_aplicadas_fuera_maleza || null,
            malezaObjetivo: layers.malezaObjetivo || layers.maleza_objetivo || null,
            malezaObjetivoOverview: layers.malezaObjetivoOverview || layers.maleza_objetivo_overview || null,
            malezaNoAplicada: layers.malezaNoAplicada || layers.maleza_no_aplicada || null,
            malezaNoAplicadaOverview: layers.malezaNoAplicadaOverview || layers.maleza_no_aplicada_overview || null,
        };
    }, [tasaVariableRemoteLayers]);

    // En vista general se usan capas overview mucho más livianas. Al acercarse
    // se cambia automáticamente a las geometrías exactas sin perder detalle.
    const [showDetailedTasaVariableLayers, setShowDetailedTasaVariableLayers] = useState(
        Number(zoom) >= VAR_DETAIL_ZOOM_THRESHOLD
    );

    const effectiveTasaVariableRemoteLayers = useMemo(() => {
        const layers = normalizedTasaVariableRemoteLayers;
        if (showDetailedTasaVariableLayers) {
            return layers;
        }
        return {
            ...layers,
            lineasAplicacion: layers.lineasAplicacionOverview || layers.lineasAplicacion,
            aplicacionPoligonos: layers.aplicacionPoligonosOverview || layers.aplicacionPoligonos,
            malezaObjetivo: layers.malezaObjetivoOverview || layers.malezaObjetivo,
            malezaNoAplicada: layers.malezaNoAplicadaOverview || layers.malezaNoAplicada,
        };
    }, [normalizedTasaVariableRemoteLayers, showDetailedTasaVariableLayers]);

    // Herbicidas VRA: la aplicación se pinta como relleno de polígonos y no como líneas.
    const isPolygonApplicationRemote =
        String(normalizedTasaVariableRemoteLayers.geometriaAplicacion || '').toUpperCase() === 'POLYGONS' &&
        Boolean(normalizedTasaVariableRemoteLayers.aplicacionPoligonos || normalizedTasaVariableRemoteLayers.lineasAplicacion);

    const handleTasaVariableMoveEnd = useCallback((event) => {
        if (!isTasaVariableMap) return;
        const mapZoom = Number(
            event?.viewState?.zoom ??
            (localMapRef.current && localMapRef.current.getMap
                ? localMapRef.current.getMap().getZoom()
                : zoom)
        );
        if (!Number.isFinite(mapZoom)) return;
        const shouldShowDetail = mapZoom >= VAR_DETAIL_ZOOM_THRESHOLD;
        setShowDetailedTasaVariableLayers((previous) =>
            previous === shouldShowDetail ? previous : shouldShowDetail
        );
    }, [isTasaVariableMap, zoom]);

    const hasRemoteTasaVariableLayers = Boolean(
        isTasaVariableMap &&
        (
            normalizedTasaVariableRemoteLayers.lineasAplicacion ||
            normalizedTasaVariableRemoteLayers.lineasAplicadasEnMaleza ||
            normalizedTasaVariableRemoteLayers.lineasAplicadasFueraMaleza ||
            normalizedTasaVariableRemoteLayers.malezaObjetivo ||
            normalizedTasaVariableRemoteLayers.malezaNoAplicada
        )
    );

    const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v12');
    const [selectedStyle, setSelectedStyle] = useState(() => {
        return localStorage.getItem('selectedMapStyle') || 'Satellite View (No Details)';
    });

    const [mapLoaded, setMapLoaded] = useState(false);
    const [selectedPopup, setSelectedPopup] = useState(null);
    const [hoverLotePopup, setHoverLotePopup] = useState(null);
    const hoveredLoteFeatureIdRef = useRef(null);
    const lastHoveredLoteIdRef = useRef(null);
    const [hasWarnedInvalidCoords, setHasWarnedInvalidCoords] = useState(false);

    const [selectedPolygonId, setSelectedPolygonId] = useState(null);
    const [selectedHarvestGrouping, setSelectedHarvestGrouping] = useState(null);

    const shouldShowIntersections = useMemo(
        () => showIntersections && !selectedHarvestGrouping,
        [showIntersections, selectedHarvestGrouping]
    );

    const safePolygonsHarvest = useMemo(() => {
        if (
            polygonsHarvestPlanning &&
            polygonsHarvestPlanning.features &&
            Array.isArray(polygonsHarvestPlanning.features)
        ) {
            return polygonsHarvestPlanning;
        }
        return { type: 'FeatureCollection', features: [] };
    }, [polygonsHarvestPlanning]);

    const safePolygonsHerbicides = useMemo(() => {
        if (
            polygonsHerbicidesPlanning &&
            polygonsHerbicidesPlanning.features &&
            Array.isArray(polygonsHerbicidesPlanning.features)
        ) {
            return polygonsHerbicidesPlanning;
        }
        return { type: 'FeatureCollection', features: [] };
    }, [polygonsHerbicidesPlanning]);

    const safePolygonsPlanning = useMemo(() => {
        return safePolygonsHerbicides.features.length > 0
            ? safePolygonsHerbicides
            : safePolygonsHarvest;
    }, [safePolygonsHerbicides, safePolygonsHarvest]);

    const harvestGroupings = useMemo(() => {
        const result = {};
        safePolygonsPlanning.features.forEach((feature) => {
            const p = feature.properties || {};
            Object.keys(p).forEach((propKey) => {
                if (propKey.startsWith('agrupacion_')) {
                    const valor = p[propKey];
                    if (valor) {
                        const colorKey = propKey.replace('agrupacion_', 'color_');
                        const colorValue = p[colorKey] || '#999999';
                        if (!result[propKey]) result[propKey] = {};
                        result[propKey][valor] = colorValue;
                    }
                }
            });
        });
        return result;
    }, [safePolygonsPlanning]);

    const harvestLegendLabels = useMemo(() => {
        if (!selectedHarvestGrouping || !harvestGroupings[selectedHarvestGrouping]) {
            return [];
        }
        const valuesMap = harvestGroupings[selectedHarvestGrouping];
        return Object.entries(valuesMap).map(([valor, color]) => ({
            color,
            text: valor,
        }));
    }, [selectedHarvestGrouping, harvestGroupings]);

    // --------------------------------------------------------------------------------
    //                  Manejo del estilo del mapa (menú de capas)
    // --------------------------------------------------------------------------------
    const mapStyles = {
        'Satellite View (No Details)': 'mapbox://styles/mapbox/satellite-v9',
        'Street Map': 'mapbox://styles/mapbox/streets-v12',
        'Terrain Map': 'mapbox://styles/mapbox/terrain-v2',
        'Outdoors Map': 'mapbox://styles/mapbox/outdoors-v12',
        'Satellite Streets': 'mapbox://styles/mapbox/satellite-streets-v12',
        'Light Map': 'mapbox://styles/mapbox/light-v10',
    };

    useEffect(() => {
        try {
            if (mapStyles[selectedStyle]) {
                setMapStyle(mapStyles[selectedStyle]);
                localStorage.setItem('selectedMapStyle', selectedStyle);
            } else {
                console.warn(`El estilo "${selectedStyle}" no está definido en mapStyles.`);
            }
        } catch (error) {
            console.error('Error al actualizar el estilo del mapa:', error);
        }
    }, [selectedStyle]);

    const handleMapLoad = useCallback(() => {
        try {
            setMapLoaded(true);
            if (mapRef && localMapRef.current) {
                mapRef.current = localMapRef.current.getMap();
            }
        } catch (error) {
            console.error('Error en handleMapLoad:', error);
        }
    }, [mapRef]);

    // --------------------------------------------------------------------------------
    //   Estilos para el popup de hover (evita que el tooltip bloquee el mouse del mapa)
    // --------------------------------------------------------------------------------
    useEffect(() => {
        try {
            const styleId = 'lote-hover-popup-style';
            if (typeof document === 'undefined') return;
            if (document.getElementById(styleId)) return;

            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .lote-hover-popup { pointer-events: none; }
                .lote-hover-popup * { pointer-events: none; }
            `;
            document.head.appendChild(style);
        } catch (e) {
            // noop
        }
    }, []);

    // --------------------------------------------------------------------------------
    //  Verificar si la URL de la imagen es de tipo "tiled" (contiene {z},{x},{y})
    // --------------------------------------------------------------------------------
    const isTiled = useMemo(() => {
        if (!imageUrl) return false;
        return imageUrl.includes('{z}') && imageUrl.includes('{x}') && imageUrl.includes('{y}');
    }, [imageUrl]);

    // --------------------------------------------------------------------------------
    //               Función para normalizar polígonos a Features
    // --------------------------------------------------------------------------------
    const getPolygonFeatures = useCallback((dataArray) => {
        try {
            if (!Array.isArray(dataArray)) {
                console.warn('getPolygonFeatures: dataArray no es un arreglo.');
                return [];
            }

            const closeRingIfNeeded = (ring) => {
                try {
                    if (!Array.isArray(ring) || ring.length === 0) return ring;
                    const first = ring[0];
                    const last = ring[ring.length - 1];
                    if (!first || !last) return ring;
                    if (first[0] !== last[0] || first[1] !== last[1]) return [...ring, first];
                    return ring;
                } catch (e) {
                    return ring;
                }
            };

            const features = dataArray
                .map((item) => {
                    try {
                        if (item && item.geometry && item.geometry.type && item.geometry.coordinates) {
                            let geometry = item.geometry;
                            if (geometry.type === 'Polygon') {
                                const coordinates = geometry.coordinates
                                    .map((ring) => {
                                        const cleaned = (ring || [])
                                            .map((coord) =>
                                                Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null
                                            )
                                            .filter((c) => c !== null);

                                        const closed = closeRingIfNeeded(cleaned);
                                        // Un anillo de polígono válido debe tener al menos 4 coords (incluye cierre)
                                        return Array.isArray(closed) && closed.length >= 4 ? closed : null;
                                    })
                                    .filter((ring) => Array.isArray(ring) && ring.length >= 4);

                                if (coordinates.length === 0) return null;

                                return {
                                    type: 'Feature',
                                    geometry: {
                                        type: 'Polygon',
                                        coordinates: coordinates,
                                    },
                                    properties: item.properties || {},
                                };
                            } else if (geometry.type === 'MultiPolygon') {
                                const coordinates = geometry.coordinates
                                    .map((polygon) => {
                                        const rings = (polygon || [])
                                            .map((ring) => {
                                                const cleaned = (ring || [])
                                                    .map((coord) =>
                                                        Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null
                                                    )
                                                    .filter((c) => c !== null);
                                                const closed = closeRingIfNeeded(cleaned);
                                                return Array.isArray(closed) && closed.length >= 4 ? closed : null;
                                            })
                                            .filter((r) => Array.isArray(r) && r.length >= 4);
                                        return rings.length > 0 ? rings : null;
                                    })
                                    .filter((p) => Array.isArray(p) && p.length > 0);

                                if (coordinates.length === 0) return null;

                                return {
                                    type: 'Feature',
                                    geometry: {
                                        type: 'MultiPolygon',
                                        coordinates: coordinates,
                                    },
                                    properties: item.properties || {},
                                };
                            }

                            console.warn(`getPolygonFeatures: Tipo de geometría "${geometry.type}" no soportado.`);
                            return null;
                        }

                        if (Array.isArray(item)) {
                            const cleaned = item
                                .map((coord) => (Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null))
                                .filter((c) => c !== null);

                            const coordinates = closeRingIfNeeded(cleaned);
                            if (Array.isArray(coordinates) && coordinates.length >= 4) {
                                return {
                                    type: 'Feature',
                                    geometry: {
                                        type: 'Polygon',
                                        coordinates: [coordinates],
                                    },
                                    properties: {},
                                };
                            }
                            return null;
                        }

                        console.warn('getPolygonFeatures: Item inválido.', item);
                        return null;
                    } catch (innerErr) {
                        console.error('Error interno procesando un item en getPolygonFeatures:', innerErr, item);
                        return null;
                    }
                })
                .filter((feature) => feature !== null);

            return features;
        } catch (error) {
            console.error('Error en getPolygonFeatures:', error);
            return [];
        }
    }, []);

    // --------------------------------------------------------------------------------
    //                   Generar datos para la capa de "points"
    // --------------------------------------------------------------------------------
    const pointData = useMemo(() => {
        try {
            if (Array.isArray(points)) {
                const pointFeatures = points
                    .map((point, index) => {
                        try {
                            if (
                                point &&
                                point.geometry &&
                                Array.isArray(point.geometry.coordinates) &&
                                point.geometry.coordinates.length >= 2
                            ) {
                                const [lon, lat] = point.geometry.coordinates;
                                if (typeof lon !== 'number' || typeof lat !== 'number') {
                                    console.warn(`Coordenadas inválidas en punto ${index}.`, point);
                                    return null;
                                }
                                return {
                                    type: 'Feature',
                                    geometry: { type: 'Point', coordinates: [lon, lat] },
                                    properties: {
                                        ...(point.properties || {}),
                                        color: point.color || point.properties?.color || '#FF0000',
                                    },
                                };
                            }
                            return null;
                        } catch (err) {
                            console.error('Error procesando un punto en pointData:', err, point);
                            return null;
                        }
                    })
                    .filter(Boolean);

                return { type: 'FeatureCollection', features: pointFeatures };
            }

            console.warn('pointData: "points" no es un arreglo.');
            return { type: 'FeatureCollection', features: [] };
        } catch (error) {
            console.error('Error en pointData useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [points]);

    // --------------------------------------------------------------------------------
    //                   Generar datos para la capa de "lines"
    // --------------------------------------------------------------------------------
    const lineData = useMemo(() => {
        try {
            if (!Array.isArray(lines)) {
                console.warn('lineData: "lines" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }

            const lineFeatures = lines
                .map((line, index) => {
                    try {
                        if (line && Array.isArray(line.latlngs)) {
                            const coordinates = line.latlngs
                                .map((latlng) => {
                                    if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
                                        return [latlng.lng, latlng.lat];
                                    }
                                    console.warn(`lineData: Coordenada inválida en línea index ${index}.`, latlng);
                                    return null;
                                })
                                .filter((coord) => coord !== null);

                            if (coordinates.length > 0) {
                                const featureId = line.id !== undefined ? line.id : `line-${index}`;
                                return {
                                    type: 'Feature',
                                    id: featureId,
                                    geometry: { type: 'LineString', coordinates },
                                    properties: {
                                        ...line.properties,
                                        color: line?.properties?.color || '#9b00a9',
                                    },
                                };
                            }
                        } else {
                            console.warn(`lineData: Estructura de línea inválida en index ${index}.`, line);
                        }
                        return null;
                    } catch (err) {
                        console.error('Error procesando un item en lineData:', err, line);
                        return null;
                    }
                })
                .filter(Boolean);

            return { type: 'FeatureCollection', features: lineFeatures };
        } catch (error) {
            console.error('Error en lineData useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [lines]);

    // --------------------------------------------------------------------------------
    //                   Generar datos para "lineasNoFiltradas"
    // --------------------------------------------------------------------------------
    const lineasNoFiltradasData = useMemo(() => {
        try {
            if (!Array.isArray(lineasNoFiltradas)) {
                console.warn('lineasNoFiltradasData: "lineasNoFiltradas" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }

            const features = lineasNoFiltradas
                .map((line, index) => {
                    try {
                        if (line && Array.isArray(line.latlngs)) {
                            const coordinates = line.latlngs
                                .map((latlng) => {
                                    if (latlng && typeof latlng.lat === 'number' && typeof latlng.lng === 'number') {
                                        return [latlng.lng, latlng.lat];
                                    }
                                    console.warn(`lineasNoFiltradasData: Coordenada inválida en index ${index}`, latlng);
                                    return null;
                                })
                                .filter((c) => c !== null);

                            if (coordinates.length > 0) {
                                return {
                                    type: 'Feature',
                                    id: line.id !== undefined ? line.id : `no-filtradas-line-${index}`,
                                    geometry: { type: 'LineString', coordinates },
                                    properties: {
                                        ...line.properties,
                                        color: '#ffffff',
                                    },
                                };
                            }
                        } else if (line && line.geometry) {
                            if (['LineString', 'MultiLineString'].includes(line.geometry.type)) {
                                return {
                                    type: 'Feature',
                                    id: line.id !== undefined ? line.id : `no-filtradas-geom-${index}`,
                                    geometry: line.geometry,
                                    properties: {
                                        ...line.properties,
                                        color: '#ffffff',
                                    },
                                };
                            }
                            console.warn(
                                `lineasNoFiltradasData: Tipo de geometría no soportado en index ${index}.`,
                                line.geometry.type
                            );
                        } else {
                            console.warn(`lineasNoFiltradasData: Línea inválida en index ${index}.`, line);
                        }
                        return null;
                    } catch (err) {
                        console.error('Error procesando un ítem en lineasNoFiltradasData:', err, line);
                        return null;
                    }
                })
                .filter(Boolean);

            return { type: 'FeatureCollection', features };
        } catch (error) {
            console.error('Error en lineasNoFiltradasData useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [lineasNoFiltradas]);

    // --------------------------------------------------------------------------------
    //            Obtener ID de Lote para los polígonos (si aplica)
    // --------------------------------------------------------------------------------
    const getLoteId = (properties) => {
        try {
            if (!properties || typeof properties !== 'object') {
                console.warn('getLoteId: properties no es un objeto válido.', properties);
                return 'Sin ID';
            }

            const keys = Object.keys(properties).map((key) => key.toLowerCase());
            const idLoteIndex = keys.indexOf('id_lote');
            const idIndex = keys.indexOf('id');

            if (idLoteIndex !== -1) {
                return properties[Object.keys(properties)[idLoteIndex]] || 'Sin ID';
            }
            if (idIndex !== -1) {
                return properties[Object.keys(properties)[idIndex]] || 'Sin ID';
            }
            return 'Sin ID';
        } catch (error) {
            console.error('Error en getLoteId:', error);
            return 'Sin ID';
        }
    };

    const getFeatureLoteId = useCallback((properties = {}) => {
        try {
            if (!properties || typeof properties !== 'object') return 'Sin ID';

            const lower = {};
            const canon = {};

            Object.keys(properties).forEach((k) => {
                const lk = String(k).toLowerCase();
                lower[lk] = properties[k];
                const ck = lk.replace(/[^a-z0-9]/g, '');
                if (!Object.prototype.hasOwnProperty.call(canon, ck)) {
                    canon[ck] = properties[k];
                }
            });

            const pick = (...keys) => {
                for (const key of keys) {
                    if (!key) continue;
                    const lk = String(key).toLowerCase();
                    const ck = lk.replace(/[^a-z0-9]/g, '');
                    const v = properties[key] ?? lower[lk] ?? canon[ck];
                    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
                }
                return null;
            };

            // Orden importante:
            // 1) IDs estándar del sistema (lo más estable para selección/recorte)
            // 2) Variantes comunes en lotes (LOTE / LOTE N / LOTE 1)
            // 3) IDs típicos de ESRI/ArcGIS (OBJECTID/OID/FID)
            // 4) IDs genéricos (id/ID/_id)
            const candidate = pick(
                // --- IDs estándar del sistema ---
                'loteId',
                'LOTE_ID',
                'lote_id',
                'ID_LOTE',
                'id_lote',
                'LOTEID',
                'LoteID',
                'LOTEID_1',
                // --- Variantes comunes en datos externos ---
                'LOTE N',
                'LOTE_N',
                'LOTEN',
                'LOTE',
                'NUM_LOTE',
                'LOTE_NUM',
                'LOTES',
                'LOTE 1',
                'LOTE_1',
                'LOTE1',
                // --- ESRI/ArcGIS ---
                'OBJECTID',
                'OBJECTID_1',
                'OID',
                'FID',
                // --- IDs genéricos ---
                '_id',
                'id',
                'ID'
            );

            return (candidate ?? 'Sin ID').toString();
        } catch (e) {
            console.error('getFeatureLoteId error:', e);
            return 'Sin ID';
        }
    }, []);

    const activeLotesSet = useMemo(() => {
        return new Set((activeLotes || []).map((v) => (v ?? '').toString()).filter((v) => v && v !== 'Sin ID'));
    }, [activeLotes]);

    const filterFCByActiveLotes = useCallback(
        (fc) => {
            if (!fc || !Array.isArray(fc.features)) return { type: 'FeatureCollection', features: [] };
            if (!activeLotes || activeLotes.length === 0) return fc;
            if (activeLotesSet.size === 0) return fc;

            return {
                ...fc,
                features: fc.features.filter((f) => activeLotesSet.has(getFeatureLoteId(f.properties || {}))),
            };
        },
        [activeLotes, activeLotesSet, getFeatureLoteId]
    );

    // --------------------------------------------------------------------------------
    //  Normalizar IDs de features (necesario para feature-state: requiere feature.id)
    //  - Evita: "feature id parameter must be provided"
    // --------------------------------------------------------------------------------
    const normalizeFCWithIds = useCallback(
        (fc, prefix = 'feature') => {
            try {
                if (!fc || fc.type !== 'FeatureCollection' || !Array.isArray(fc.features)) {
                    return { type: 'FeatureCollection', features: [] };
                }

                const normalized = fc.features
                    .map((f, idx) => {
                        if (!f || typeof f !== 'object') return null;
                        const props = f.properties && typeof f.properties === 'object' ? f.properties : {};

                        // Preferimos un ID estable asociado al lote si existe.
                        const loteId = getFeatureLoteId(props);

                        const preferredId =
                            f.id ??
                            props.id ??
                            props._id ??
                            props.ID ??
                            props.ID_LOTE ??
                            props.LOTE_ID ??
                            props.LOTEID ??
                            props.loteId ??
                            props.id_lote ??
                            props.lote_id;

                        const idValue =
                            preferredId !== undefined && preferredId !== null && String(preferredId).trim() !== ''
                                ? preferredId
                                : loteId !== undefined && loteId !== null && String(loteId).trim() !== '' && loteId !== 'Sin ID'
                                    ? loteId
                                    : `${prefix}-${idx}`;

                        return {
                            ...f,
                            id: String(idValue),
                            properties: props,
                        };
                    })
                    .filter(Boolean);

                return { ...fc, features: normalized };
            } catch (e) {
                console.error('normalizeFCWithIds error:', e);
                return { type: 'FeatureCollection', features: [] };
            }
        },
        [getFeatureLoteId]
    );

    // --------------------------------------------------------------------------------
    //   LotesGeojson (visibleLotePolygons): capa base de lotes con IDs para hover/popup
    // --------------------------------------------------------------------------------
    const lotesGeojsonData = useMemo(() => {
        try {
            const fc =
                visibleLotePolygons && visibleLotePolygons.type === 'FeatureCollection'
                    ? visibleLotePolygons
                    : { type: 'FeatureCollection', features: [] };

            const feats = Array.isArray(fc.features) ? fc.features : [];

            const normalized = feats
                .map((f, idx) => {
                    if (!f || typeof f !== 'object') return null;
                    const p = f.properties && typeof f.properties === 'object' ? f.properties : {};
                    const idCandidate =
                        f.id ??
                        p.id ??
                        p._id ??
                        p.ID ??
                        p.ID_LOTE ??
                        p.LOTE_ID ??
                        p.loteId ??
                        p.id_lote ??
                        p.lote_id ??
                        `lote-geojson-${idx}`;

                    return {
                        ...f,
                        id: String(idCandidate),
                        properties: p,
                    };
                })
                .filter(Boolean);

            return { type: 'FeatureCollection', features: normalized };
        } catch (e) {
            console.error('Error preparando lotesGeojsonData:', e);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [visibleLotePolygons]);

    // --------------------------------------------------------------------------------
    //   Datos de polígonos provenientes de polygonsData (ya en formato GeoJSON)
    // --------------------------------------------------------------------------------
    const polygonDataFeatures = useMemo(() => {
        try {
            if (!Array.isArray(polygonsData)) {
                console.warn('polygonDataFeatures: "polygonsData" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }

            const features = polygonsData
                .map((feature, index) => {
                    try {
                        if (!feature || typeof feature !== 'object') {
                            console.warn(`polygonDataFeatures: Item inválido en index ${index}`, feature);
                            return null;
                        }

                        if (!feature.geometry || typeof feature.geometry !== 'object') {
                            console.warn(`polygonDataFeatures: geometry ausente o inválida en index ${index}`, feature);
                            return null;
                        }

                        const { type, coordinates } = feature.geometry;
                        if (!type || !Array.isArray(coordinates)) {
                            console.warn(`polygonDataFeatures: geometry con formato inválido en index ${index}`, feature);
                            return null;
                        }

                        const cleanPolygon = (coordsArray) => {
                            return coordsArray
                                .map((coord) => {
                                    if (Array.isArray(coord) && coord.length >= 2) {
                                        const [lng, lat] = coord;
                                        if (typeof lng === 'number' && typeof lat === 'number') {
                                            return [lng, lat];
                                        }
                                    }
                                    return null;
                                })
                                .filter(Boolean);
                        };

                        if (type === 'Polygon') {
                            const validCoordinates = coordinates
                                .map((ring) => (Array.isArray(ring) ? cleanPolygon(ring) : []))
                                .filter((ring) => ring.length > 0);

                            if (validCoordinates.length === 0) {
                                console.warn(`polygonDataFeatures: Polygon vacío/invalid en index ${index}.`);
                                return null;
                            }

                            return {
                                type: 'Feature',
                                id: feature.id !== undefined ? feature.id : `polygons-data-feature-${index}`,
                                geometry: {
                                    type: 'Polygon',
                                    coordinates: validCoordinates,
                                },
                                properties: {
                                    ...feature.properties,
                                },
                            };
                        }

                        if (type === 'MultiPolygon') {
                            const validCoordinates = coordinates
                                .map((polygon) => {
                                    if (!Array.isArray(polygon)) return null;
                                    return polygon
                                        .map((ring) => (Array.isArray(ring) ? cleanPolygon(ring) : []))
                                        .filter((r) => r.length > 0);
                                })
                                .filter((poly) => Array.isArray(poly) && poly.length > 0);

                            if (validCoordinates.length === 0) {
                                console.warn(`polygonDataFeatures: MultiPolygon vacío/invalid en index ${index}.`);
                                return null;
                            }

                            return {
                                type: 'Feature',
                                id: feature.id !== undefined ? feature.id : `polygons-data-multi-feature-${index}`,
                                geometry: {
                                    type: 'MultiPolygon',
                                    coordinates: validCoordinates,
                                },
                                properties: {
                                    ...feature.properties,
                                },
                            };
                        }

                        console.warn(`polygonDataFeatures: Tipo de geometría "${type}" no soportado en index ${index}.`);
                        return null;
                    } catch (innerErr) {
                        console.error('Error interno procesando polygonDataFeatures item:', innerErr, feature);
                        return null;
                    }
                })
                .filter(Boolean);

            const fc = { type: 'FeatureCollection', features };
            return filterFCByActiveLotes(fc);
        } catch (error) {
            console.error('Error en polygonDataFeatures useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [polygonsData, filterFCByActiveLotes]);

    const polygonDataMemo = useMemo(() => {
        try {
            if (!Array.isArray(polygons)) {
                console.warn('polygonDataMemo: "polygons" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }

            let foundInvalidCoords = false;

            let largestPolygonIndex = -1;
            let largestPolygonArea = 0;

            if (lines.length > 0 && polygons.length > 0) {
                polygons.forEach((polygonCoords, index) => {
                    try {
                        let coordsForTurf = null;

                        if (Array.isArray(polygonCoords)) {
                            if (polygonCoords.length > 2) {
                                coordsForTurf = polygonCoords
                                    .map((coord) =>
                                        Array.isArray(coord) && coord.length >= 2 ? [coord[1], coord[0]] : null
                                    )
                                    .filter(Boolean);
                            }
                        } else if (
                            polygonCoords &&
                            typeof polygonCoords === 'object' &&
                            polygonCoords.geometry &&
                            Array.isArray(polygonCoords.geometry.coordinates)
                        ) {
                            const { geometry } = polygonCoords;

                            if (geometry.type === 'Polygon') {
                                let raw = geometry.coordinates;
                                if (!Array.isArray(raw[0])) {
                                    raw = [raw];
                                }
                                coordsForTurf = raw[0]
                                    .map((coord) =>
                                        Array.isArray(coord) && coord.length >= 2 ? [coord[1], coord[0]] : null
                                    )
                                    .filter(Boolean);
                            } else if (geometry.type === 'MultiPolygon') {
                                const raw = geometry.coordinates;
                                if (Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0]) && Array.isArray(raw[0][0])) {
                                    coordsForTurf = raw[0][0]
                                        .map((coord) =>
                                            Array.isArray(coord) && coord.length >= 2 ? [coord[1], coord[0]] : null
                                        )
                                        .filter(Boolean);
                                }
                            }
                        }

                        if (coordsForTurf && coordsForTurf.length > 2) {
                            const turfPolygon = turf.polygon([coordsForTurf]);
                            const area = turf.area(turfPolygon);
                            if (area > largestPolygonArea) {
                                largestPolygonArea = area;
                                largestPolygonIndex = index;
                            }
                        }
                    } catch (errLargest) {
                        console.error('Error calculando área de un polígono:', errLargest);
                    }
                });
            }

            const polygonFeatures = polygons
                .map((polygonCoords, index) => {
                    try {
                        let coordinates = null;
                        let properties = {};
                        let idFeature = `polygon-memo-${index}`;

                        if (Array.isArray(polygonCoords)) {
                            const loteId = getLoteId(polygonProperties[index] || {});
                            const isActive = activeLotes.includes(loteId);
                            const originalProps = polygonProperties[index] || {};
                            const finalProps = {
                                ...originalProps,
                                ...((shouldShowIntersections && originalProps.INTERSECCION === 'YES') && { color: 'red' }),
                            };

                            if (!isTasaVariableMap && index === largestPolygonIndex && largestPolygonIndex !== -1) {
                                finalProps.color = 'yellow';
                            }

                            coordinates = polygonCoords
                                .map((coord) => (Array.isArray(coord) && coord.length >= 2 ? [coord[1], coord[0]] : null))
                                .filter((c) => c !== null);

                            if (coordinates.length === 0) {
                                foundInvalidCoords = true;
                                return null;
                            }

                            properties = {
                                active: isActive,
                                loteId,
                                ...finalProps,
                            };
                        } else if (
                            polygonCoords &&
                            typeof polygonCoords === 'object' &&
                            polygonCoords.geometry &&
                            Array.isArray(polygonCoords.geometry.coordinates)
                        ) {
                            const { geometry } = polygonCoords;
                            let rawCoords = geometry.coordinates;

                            if (geometry.type === 'Polygon') {
                                if (!Array.isArray(rawCoords[0])) {
                                    rawCoords = [rawCoords];
                                }

                                const convertedRings = rawCoords.map((ring) =>
                                    ring
                                        .map((c) => (Array.isArray(c) && c.length >= 2 ? [c[1], c[0]] : null))
                                        .filter(Boolean)
                                );

                                if (convertedRings.length < 1 || convertedRings[0].length < 3) {
                                    foundInvalidCoords = true;
                                    return null;
                                }

                                const first = convertedRings[0][0];
                                const last = convertedRings[0][convertedRings[0].length - 1];
                                if (first[0] !== last[0] || first[1] !== last[1]) {
                                    convertedRings[0].push(first);
                                }

                                coordinates = convertedRings[0];
                                properties = polygonCoords.properties || {};

                                if (shouldShowIntersections && properties.INTERSECCION === 'YES') {
                                    properties.color = 'red';
                                }

                                idFeature = polygonCoords.id || `polygon-memo-${index}`;
                            } else if (geometry.type === 'MultiPolygon') {
                                const raw = geometry.coordinates;
                                if (Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0]) && Array.isArray(raw[0][0])) {
                                    const ring = raw[0][0].map((c) => (Array.isArray(c) && c.length >= 2 ? [c[1], c[0]] : null));
                                    coordinates = ring.filter(Boolean);
                                    properties = polygonCoords.properties || {};

                                    if (shouldShowIntersections && properties.INTERSECCION === 'YES') {
                                        properties.color = 'red';
                                    }

                                    idFeature = polygonCoords.id || `polygon-memo-${index}`;
                                }
                            }
                        } else {
                            foundInvalidCoords = true;
                            return null;
                        }

                        if (!coordinates || coordinates.length < 4) {
                            foundInvalidCoords = true;
                            return null;
                        }

                        const first = coordinates[0];
                        const last = coordinates[coordinates.length - 1];
                        if (first[0] !== last[0] || first[1] !== last[1]) {
                            coordinates.push(first);
                        }

                        return {
                            type: 'Feature',
                            id: idFeature,
                            geometry: {
                                type: 'Polygon',
                                coordinates: [coordinates],
                            },
                            properties,
                        };
                    } catch (innerErr) {
                        console.error('Error interno procesando un polígono en polygonDataMemo:', innerErr, polygonCoords);
                        return null;
                    }
                })
                .filter(Boolean);

            if (foundInvalidCoords && !hasWarnedInvalidCoords) {
                console.warn('polygonDataMemo: Se encontraron coordenadas inválidas en al menos un polígono.');
                setHasWarnedInvalidCoords(true);
            }

            const normalFeatures = [];
            const intersectionFeatures = [];

            polygonFeatures.forEach((feature) => {
                if (feature.properties.INTERSECCION === 'YES') {
                    intersectionFeatures.push(feature);
                } else {
                    normalFeatures.push(feature);
                }
            });

            const finalFeatures = shouldShowIntersections ? [...normalFeatures, ...intersectionFeatures] : normalFeatures;

            const filteredFeatures =
                activeLotes.length === 0 ? finalFeatures : finalFeatures.filter((feat) => activeLotes.includes(feat.properties.loteId));

            return { type: 'FeatureCollection', features: filteredFeatures };
        } catch (error) {
            console.error('Error en polygonDataMemo useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [polygons, activeLotes, polygonProperties, hasWarnedInvalidCoords, lines, shouldShowIntersections, isTasaVariableMap]);

    // --------------------------------------------------------------------------------
    //   Datos de "bufferedLines" (polígonos buffer de las líneas)
    // --------------------------------------------------------------------------------
    const bufferedLinesData = useMemo(() => {
        try {
            if (!Array.isArray(bufferedLines)) {
                console.warn('bufferedLinesData: "bufferedLines" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }
            const features = getPolygonFeatures(bufferedLines);
            return { type: 'FeatureCollection', features };
        } catch (error) {
            console.error('Error en bufferedLinesData useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [bufferedLines, getPolygonFeatures]);

    // --------------------------------------------------------------------------------
    //    Datos de "bufferedIntersections" (intersecciones bufferizadas)
    // --------------------------------------------------------------------------------
    const bufferedIntersectionsData = useMemo(() => {
        console.log("INTERSECCIONES BUFFERIZADAS:", bufferedIntersections);
        try {
            if (!Array.isArray(bufferedIntersections)) {
                console.warn('bufferedIntersectionsData: "bufferedIntersections" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }
            const features = getPolygonFeatures(bufferedIntersections);
            return { type: 'FeatureCollection', features };
        } catch (error) {
            console.error('Error en bufferedIntersectionsData useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [bufferedIntersections, getPolygonFeatures]);

    // --------------------------------------------------------------------------------
    //               Datos de áreas superpuestas (areasSuperpuestas)
    // --------------------------------------------------------------------------------
    const areasSuperpuestasData = useMemo(() => {
        try {
            if (!Array.isArray(areasSuperpuestas)) {
                console.warn('areasSuperpuestasData: "areasSuperpuestas" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }

            const processedAreas = areasSuperpuestas
                .map((item) => {
                    try {
                        if (item && item.geometry && item.geometry.coordinates) {
                            let geometry = item.geometry;

                            if (geometry.type === 'Polygon') {
                                geometry = {
                                    ...geometry,
                                    coordinates: geometry.coordinates
                                        .map((ring) =>
                                            ring
                                                .map((coord) =>
                                                    Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null
                                                )
                                                .filter((c) => c !== null)
                                        )
                                        .filter((ring) => ring.length > 0),
                                };
                            } else if (geometry.type === 'MultiPolygon') {
                                geometry = {
                                    ...geometry,
                                    coordinates: geometry.coordinates
                                        .map((polygon) =>
                                            polygon
                                                .map((ring) =>
                                                    ring
                                                        .map((coord) =>
                                                            Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null
                                                        )
                                                        .filter((c) => c !== null)
                                                )
                                                .filter((ring) => ring.length > 0)
                                        )
                                        .filter((polygon) => polygon.length > 0),
                                };
                            }

                            return { ...item, geometry };
                        }

                        if (Array.isArray(item)) {
                            const coordinates = item
                                .map((coord) => (Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null))
                                .filter((c) => c !== null);

                            if (coordinates.length > 0) {
                                return {
                                    type: 'Feature',
                                    geometry: {
                                        type: 'Polygon',
                                        coordinates: [coordinates],
                                    },
                                    properties: {},
                                };
                            }
                        }

                        console.warn('areasSuperpuestasData: Item inválido.', item);
                        return null;
                    } catch (innerErr) {
                        console.error('Error interno procesando un item en areasSuperpuestasData:', innerErr, item);
                        return null;
                    }
                })
                .filter(Boolean);

            const features = getPolygonFeatures(processedAreas);
            return { type: 'FeatureCollection', features };
        } catch (error) {
            console.error('Error en areasSuperpuestasData useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [areasSuperpuestas, getPolygonFeatures]);

    // --------------------------------------------------------------------------------
    //           Datos de áreas NO intersectadas (nonIntersectedAreas)
    // --------------------------------------------------------------------------------
    const nonIntersectedAreasData = useMemo(() => {
        try {
            if (!Array.isArray(nonIntersectedAreas)) {
                console.warn('nonIntersectedAreasData: "nonIntersectedAreas" no es un arreglo.');
                return { type: 'FeatureCollection', features: [] };
            }

            const processedAreas = nonIntersectedAreas
                .map((item) => {
                    try {
                        if (item && item.geometry && item.geometry.coordinates) {
                            let geometry = item.geometry;

                            if (geometry.type === 'Polygon') {
                                geometry = {
                                    ...geometry,
                                    coordinates: geometry.coordinates
                                        .map((ring) =>
                                            ring
                                                .map((coord) =>
                                                    Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null
                                                )
                                                .filter((c) => c !== null)
                                        )
                                        .filter((ring) => ring.length > 0),
                                };
                            } else if (geometry.type === 'MultiPolygon') {
                                geometry = {
                                    ...geometry,
                                    coordinates: geometry.coordinates
                                        .map((polygon) =>
                                            polygon
                                                .map((ring) =>
                                                    ring
                                                        .map((coord) =>
                                                            Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null
                                                        )
                                                        .filter((c) => c !== null)
                                                )
                                                .filter((ring) => ring.length > 0)
                                        )
                                        .filter((polygon) => polygon.length > 0),
                                };
                            }

                            return { ...item, geometry };
                        }

                        if (Array.isArray(item)) {
                            const coordinates = item
                                .map((coord) => (Array.isArray(coord) && coord.length >= 2 ? [coord[0], coord[1]] : null))
                                .filter((c) => c !== null);

                            if (coordinates.length > 0) {
                                return {
                                    type: 'Feature',
                                    geometry: {
                                        type: 'Polygon',
                                        coordinates: [coordinates],
                                    },
                                    properties: {},
                                };
                            }
                        }

                        console.warn('nonIntersectedAreasData: Item inválido.', item);
                        return null;
                    } catch (innerErr) {
                        console.error('Error interno procesando un item en nonIntersectedAreasData:', innerErr, item);
                        return null;
                    }
                })
                .filter(Boolean);

            const features = getPolygonFeatures(processedAreas);
            return { type: 'FeatureCollection', features };
        } catch (error) {
            console.error('Error en nonIntersectedAreasData useMemo:', error);
            return { type: 'FeatureCollection', features: [] };
        }
    }, [nonIntersectedAreas, getPolygonFeatures]);

    // --------------------------------------------------------------------------------
    //    Ajuste automático de bounds según datos que existan (una sola vez)
    // --------------------------------------------------------------------------------
    const hasFitBoundsRef = useRef({
        points: false,
        lines: false,
        polygons: false,
        polygonsData: false,
        lineasNoFiltradas: false,
        image: false,
    });

    const fitBoundsToData = useCallback((dataFeatures) => {
        try {
            if (localMapRef.current && localMapRef.current.getMap()) {
                const map = localMapRef.current.getMap();
                const bounds = new mapboxgl.LngLatBounds();

                dataFeatures.forEach((feature) => {
                    try {
                        const geometry = feature.geometry;
                        if (!geometry) return;

                        const { type, coordinates } = geometry;
                        switch (type) {
                            case 'Point':
                                bounds.extend(coordinates);
                                break;
                            case 'LineString':
                                coordinates.forEach((coord) => bounds.extend(coord));
                                break;
                            case 'Polygon':
                                coordinates.forEach((ring) => ring.forEach((coord) => bounds.extend(coord)));
                                break;
                            case 'MultiPolygon':
                                coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach((coord) => bounds.extend(coord))));
                                break;
                            default:
                                break;
                        }
                    } catch (geomErr) {
                        console.error('Error procesando feature en fitBoundsToData:', geomErr, feature);
                    }
                });

                if (!bounds.isEmpty()) {
                    map.fitBounds(bounds, { padding: 20 });
                }
            }
        } catch (error) {
            console.error('Error en fitBoundsToData:', error);
        }
    }, []);

    useEffect(() => {
        try {
            if (mapLoaded && localMapRef.current && localMapRef.current.getMap()) {
                const map = localMapRef.current.getMap();
                const bounds = new mapboxgl.LngLatBounds();
                let shouldFitBounds = false;

                // 1) Imagen overlay
                if (imageUrl && northWestCoords && southEastCoords && !hasFitBoundsRef.current.image) {
                    try {
                        bounds.extend([northWestCoords[0], northWestCoords[1]]);
                        bounds.extend([southEastCoords[0], northWestCoords[1]]);
                        bounds.extend([southEastCoords[0], southEastCoords[1]]);
                        bounds.extend([northWestCoords[0], southEastCoords[1]]);
                        hasFitBoundsRef.current.image = true;
                        shouldFitBounds = true;
                    } catch (errImg) {
                        console.error('Error al ajustar bounds para image overlay:', errImg);
                    }
                }

                // 2) Puntos
                if (!isTasaVariableMap && pointData.features.length > 0 && !hasFitBoundsRef.current.points) {
                    try {
                        pointData.features.forEach((feature) => {
                            if (feature.geometry && feature.geometry.coordinates) {
                                bounds.extend(feature.geometry.coordinates);
                            }
                        });
                        hasFitBoundsRef.current.points = true;
                        shouldFitBounds = true;
                    } catch (errPoints) {
                        console.error('Error al ajustar bounds para puntos:', errPoints);
                    }
                }

                // 3) Líneas
                if (lineData.features.length > 0 && !hasFitBoundsRef.current.lines) {
                    try {
                        lineData.features.forEach((feature) => {
                            if (feature.geometry && feature.geometry.type === 'LineString') {
                                feature.geometry.coordinates.forEach((coord) => bounds.extend(coord));
                            }
                        });
                        hasFitBoundsRef.current.lines = true;
                        shouldFitBounds = true;
                    } catch (errLines) {
                        console.error('Error al ajustar bounds para líneas:', errLines);
                    }
                }

                // 4) Líneas no filtradas
                if (lineasNoFiltradasData.features.length > 0 && !hasFitBoundsRef.current.lineasNoFiltradas) {
                    try {
                        lineasNoFiltradasData.features.forEach((feature) => {
                            if (feature.geometry.type === 'LineString') {
                                feature.geometry.coordinates.forEach((coord) => bounds.extend(coord));
                            } else if (feature.geometry.type === 'MultiLineString') {
                                feature.geometry.coordinates.forEach((line) => line.forEach((coord) => bounds.extend(coord)));
                            }
                        });
                        hasFitBoundsRef.current.lineasNoFiltradas = true;
                        shouldFitBounds = true;
                    } catch (errNoFiltradas) {
                        console.error('Error al ajustar bounds para líneas no filtradas:', errNoFiltradas);
                    }
                }

                // 5) Polígonos "polygonDataMemo"
                if (polygonDataMemo.features.length > 0 && !hasFitBoundsRef.current.polygons) {
                    try {
                        polygonDataMemo.features.forEach((feature) => {
                            if (feature.geometry && feature.geometry.type === 'Polygon') {
                                feature.geometry.coordinates.forEach((ring) => ring.forEach((coord) => bounds.extend(coord)));
                            } else if (feature.geometry && feature.geometry.type === 'MultiPolygon') {
                                feature.geometry.coordinates.forEach((polygon) =>
                                    polygon.forEach((ring) => ring.forEach((coord) => bounds.extend(coord)))
                                );
                            }
                        });
                        hasFitBoundsRef.current.polygons = true;
                        shouldFitBounds = true;
                    } catch (errPolygons) {
                        console.error('Error al ajustar bounds para polígonos:', errPolygons);
                    }
                }

                // 6) Polígonos "polygonDataFeatures"
                if (polygonDataFeatures.features.length > 0 && !hasFitBoundsRef.current.polygonsData) {
                    try {
                        polygonDataFeatures.features.forEach((feature) => {
                            if (feature.geometry && feature.geometry.type === 'Polygon') {
                                feature.geometry.coordinates.forEach((ring) => ring.forEach((coord) => bounds.extend(coord)));
                            } else if (feature.geometry && feature.geometry.type === 'MultiPolygon') {
                                feature.geometry.coordinates.forEach((polygon) =>
                                    polygon.forEach((ring) => ring.forEach((coord) => bounds.extend(coord)))
                                );
                            }
                        });
                        hasFitBoundsRef.current.polygonsData = true;
                        shouldFitBounds = true;
                    } catch (errPolygonsData) {
                        console.error('Error al ajustar bounds para polígonos de datos:', errPolygonsData);
                    }
                }

                if (shouldFitBounds && !bounds.isEmpty()) {
                    try {
                        map.fitBounds(bounds, { padding: 20 });
                    } catch (errFit) {
                        console.error('Error al llamar a fitBounds:', errFit);
                    }
                }
            }
        } catch (error) {
            console.error('Error en el useEffect de fitBounds:', error);
        }
    }, [
        mapLoaded,
        pointData.features.length,
        lineData.features.length,
        polygonDataMemo.features.length,
        polygonDataFeatures.features.length,
        isTasaVariableMap,
        lineasNoFiltradasData.features.length,
        imageUrl,
        northWestCoords,
        southEastCoords,
        fitBoundsToData,
    ]);

    // --------------------------------------------------------------------------------
    //  Botones para centrar el mapa en "polygonsData" o en el "analysis" (líneas/puntos)
    // --------------------------------------------------------------------------------
    const handleCenterToPolygonsData = useCallback(() => {
        try {
            if (polygonDataFeatures.features.length > 0 && localMapRef.current) {
                fitBoundsToData(polygonDataFeatures.features);
            } else {
                console.warn('handleCenterToPolygonsData: No hay características de polígonos para centrar.');
            }
        } catch (error) {
            console.error('Error en handleCenterToPolygonsData:', error);
        }
    }, [polygonDataFeatures.features, fitBoundsToData]);

    const handleCenterToAnalysis = useCallback(() => {
        try {
            if (localMapRef.current) {
                const map = localMapRef.current.getMap();
                if (
                    isTasaVariableMap &&
                    Array.isArray(tasaVariableBounds) &&
                    tasaVariableBounds.length === 2 &&
                    Array.isArray(tasaVariableBounds[0]) &&
                    Array.isArray(tasaVariableBounds[1]) &&
                    [...tasaVariableBounds[0], ...tasaVariableBounds[1]].every(Number.isFinite)
                ) {
                    map.fitBounds(tasaVariableBounds, { padding: 40, maxZoom: 17 });
                } else if (isTasaVariableMap && polygonDataMemo.features.length > 0) {
                    fitBoundsToData(polygonDataMemo.features);
                } else if (pointData.features.length > 0) {
                    fitBoundsToData(pointData.features);
                } else if (lineData.features.length > 0) {
                    fitBoundsToData(lineData.features);
                } else if (polygonDataMemo.features.length > 0) {
                    fitBoundsToData(polygonDataMemo.features);
                } else if (imageUrl && northWestCoords && southEastCoords) {
                    const bounds = new mapboxgl.LngLatBounds();
                    bounds.extend([northWestCoords[0], northWestCoords[1]]);
                    bounds.extend([southEastCoords[0], northWestCoords[1]]);
                    bounds.extend([southEastCoords[0], southEastCoords[1]]);
                    bounds.extend([northWestCoords[0], southEastCoords[1]]);
                    map.fitBounds(bounds, { padding: 20 });
                } else {
                    console.warn('handleCenterToAnalysis: No hay datos disponibles para centrar.');
                }
            }
        } catch (error) {
            console.error('Error en handleCenterToAnalysis:', error);
        }
    }, [pointData.features, lineData.features, polygonDataMemo.features, imageUrl, northWestCoords, southEastCoords, fitBoundsToData, isTasaVariableMap, tasaVariableBounds]);

    // --------------------------------------------------------------------------------
    //  Manejo de eventos (click, hover, mouseout) en las "lines" (lines-layer)
    // --------------------------------------------------------------------------------
    const onLineClickRef = useRef(onLineClick);
    const onLineHoverRef = useRef(onLineHover);
    const onLineMouseOutRef = useRef(onLineMouseOut);

    useEffect(() => {
        onLineClickRef.current = onLineClick;
    }, [onLineClick]);

    useEffect(() => {
        onLineHoverRef.current = onLineHover;
    }, [onLineHover]);

    useEffect(() => {
        onLineMouseOutRef.current = onLineMouseOut;
    }, [onLineMouseOut]);

    useEffect(() => {
        try {
            if (!mapLoaded || !localMapRef.current || !localMapRef.current.getMap()) return;

            const map = localMapRef.current.getMap();

            const handleClick = (e) => {
                try {
                    const eventLayerId = e?.features?.[0]?.layer?.id;
                    const hitboxExists = !!map.getLayer('lines-layer-hitbox');
                    const shouldHandle = hitboxExists
                        ? eventLayerId === 'lines-layer-hitbox'
                        : eventLayerId === 'lines-layer';
                    if (!shouldHandle) return;

                    const features = e.features;
                    if (onLineClickRef.current && features && features.length > 0) {
                        onLineClickRef.current(e);
                    }
                } catch (error) {
                    console.error('handleClick en líneas:', error);
                }
            };

            const handleMouseMove = (e) => {
                try {
                    if (e.features && e.features.length > 0) {
                        const feature = e.features[0];
                        const eventLayerId = feature.layer?.id;
                        const hitboxExists = !!map.getLayer('lines-layer-hitbox');
                        const shouldHandle = hitboxExists ? eventLayerId === 'lines-layer-hitbox' : eventLayerId === 'lines-layer';

                        if (shouldHandle) {
                            map.getCanvas().style.cursor = 'pointer';
                            map.setFeatureState({ source: 'lines', id: feature.id }, { hover: true });
                            onLineHoverRef.current(feature);
                        }
                    }
                } catch (error) {
                    console.error('handleMouseMove en líneas:', error);
                }
            };

            const handleMouseLeave = (e) => {
                try {
                    if (e?.point && isPointerOnLine(map, e.point)) return;
                    map.getCanvas().style.cursor = '';
                    const features = map.querySourceFeatures('lines');
                    features.forEach((feature) => {
                        map.setFeatureState({ source: 'lines', id: feature.id }, { hover: false });
                    });
                    onLineMouseOutRef.current();
                } catch (error) {
                    console.error('handleMouseLeave en líneas:', error);
                }
            };

            const boundLineLayers = ['lines-layer-hitbox', 'lines-layer'].filter((layerId) => {
                try {
                    return Boolean(map.getLayer(layerId));
                } catch (error) {
                    return false;
                }
            });

            boundLineLayers.forEach((layerId) => {
                map.on('click', layerId, handleClick);
                map.on('mousemove', layerId, handleMouseMove);
                map.on('mouseleave', layerId, handleMouseLeave);
            });

            return () => {
                try {
                    boundLineLayers.forEach((layerId) => {
                        map.off('click', layerId, handleClick);
                        map.off('mousemove', layerId, handleMouseMove);
                        map.off('mouseleave', layerId, handleMouseLeave);
                    });
                } catch (error) {
                    console.error('Error al remover eventos de líneas:', error);
                }
            };
        } catch (error) {
            console.error('Error en useEffect de eventos de líneas:', error);
            return undefined;
        }
    }, [mapLoaded]);

    // --------------------------------------------------------------------------------
    //             Manejo de selectedFiles (cargas de capas GeoJSON/imagen)
    // --------------------------------------------------------------------------------
    const [selectedFilesData, setSelectedFilesData] = useState([]);

    const getAnalysisTypeFromFileName = (fileName) => {
        try {
            if (!fileName || typeof fileName !== 'string') return 'Desconocido';
            if (fileName.includes('aplicaciones_aereas')) return 'Aplicaciones aéreas';
            if (fileName.includes('cosecha_mecanica')) return 'Cosecha mecánica';
            if (fileName.includes('conteo_de_palmas')) return 'Conteo de palmas';
            if (fileName.includes('deteccion_de_maleza')) return 'Detección de maleza';
            return 'Desconocido';
        } catch (error) {
            console.error('Error en getAnalysisTypeFromFileName:', error);
            return 'Desconocido';
        }
    };

    const isImageFile = (fileName) => {
        try {
            if (!fileName || typeof fileName !== 'string') return false;
            const ext = fileName.split('.').pop().toLowerCase();
            return ['jpg', 'jpeg', 'tif', 'tiff', 'png'].includes(ext);
        } catch (error) {
            console.error('Error en isImageFile:', error);
            return false;
        }
    };

    useEffect(() => {
        const loadSelectedFiles = async () => {
            try {
                if (!Array.isArray(selectedFiles)) {
                    console.warn('loadSelectedFiles: "selectedFiles" no es un arreglo.');
                    setSelectedFilesData([]);
                    return;
                }

                const results = [];

                for (let i = 0; i < selectedFiles.length; i++) {
                    try {
                        const file = selectedFiles[i];
                        const analysisType = getAnalysisTypeFromFileName(file.file_name || '');
                        const date = file.updated || 'Fecha desconocida';
                        const isFiltradas = (file.file_name || '').includes('_filtradas');
                        const isNoFiltradas = (file.file_name || '').includes('_no_filtradas');

                        if (isImageFile(file.file_name)) {
                            results.push({
                                type: 'image',
                                id: `selectedfile-image-${i}`,
                                imageUrl: file.signed_url,
                                coordinates: null,
                                analysisType,
                                date,
                            });
                        } else {
                            try {
                                const resp = await axios.get(file.signed_url);
                                let data = resp.data;

                                if (data.type === 'Feature') {
                                    data = { type: 'FeatureCollection', features: [data] };
                                } else if (data.type !== 'FeatureCollection') {
                                    data = { type: 'FeatureCollection', features: [] };
                                }

                                data.features = (data.features || []).map((f) => ({
                                    ...f,
                                    properties: {
                                        ...(f.properties || {}),
                                        analysisType,
                                        date,
                                        filtradoEstado: isFiltradas
                                            ? 'filtradas'
                                            : isNoFiltradas
                                                ? 'no_filtradas'
                                                : 'filtradas',
                                    },
                                }));

                                results.push({
                                    type: 'geojson',
                                    id: `selectedfile-geojson-${i}`,
                                    data,
                                    analysisType,
                                    date,
                                });
                            } catch (fileErr) {
                                console.error('Error al cargar el archivo geojson:', fileErr);
                                results.push({
                                    type: 'geojson',
                                    id: `selectedfile-geojson-${i}`,
                                    data: { type: 'FeatureCollection', features: [] },
                                    analysisType,
                                    date,
                                });
                            }
                        }
                    } catch (loopErr) {
                        console.error(`Error en iteración de selectedFiles index ${i}:`, loopErr);
                    }
                }

                setSelectedFilesData(results);
            } catch (error) {
                console.error('Error en loadSelectedFiles:', error);
                setSelectedFilesData([]);
            }
        };

        loadSelectedFiles();
    }, [selectedFiles]);

    // --------------------------------------------------------------------------------
    //  Manejo de popup para los selectedFiles
    // --------------------------------------------------------------------------------
    const [selectedFilesPopup, setSelectedFilesPopup] = useState(null);

    const polygonLayerSelectedNormal = (id) => ({
        id: `selectedfile-polygon-layer-normal-${id}`,
        type: 'fill',
        paint: {
            'fill-color': '#198C19',
            'fill-opacity': 0.5,
            'fill-outline-color': '#198C19',
        },
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', ['get', 'type'], 'overlap']],
    });

    const polygonLayerSelectedOverlap = (id) => ({
        id: `selectedfile-polygon-layer-overlap-${id}`,
        type: 'fill',
        paint: {
            'fill-color': '#FF0000',
            'fill-opacity': 0.7,
            'fill-outline-color': '#FF0000',
        },
        filter: ['all', ['==', '$type', 'Polygon'], ['==', ['get', 'type'], 'overlap']],
    });

    const lineLayerSelected = (id) => ({
        id: `selectedfile-line-layer-${id}`,
        type: 'line',
        paint: {
            'line-color': [
                'match',
                ['get', 'filtradoEstado'],
                'filtradas',
                '#ff0000',
                'no_filtradas',
                '#8C8C8C',
                '#8C8C8C',
            ],
            'line-width': 2,
        },
        // Las líneas "filtradas" (moradas) van arriba de la ruta de vuelo, pero abajo de buffers/intersecciones.
        filter: ['all', ['==', '$type', 'LineString'], ['!=', ['get', 'filtradoEstado'], 'no_filtradas']],
    });

    // Ruta de vuelo (no_filtradas): arriba de lotes, abajo de líneas filtradas y abajo de buffers/intersecciones
    const flightRouteLineLayerSelected = (id) => ({
        id: `selectedfile-flight-layer-${id}`,
        type: 'line',
        paint: {
            'line-color': '#8C8C8C',
            'line-width': 2,
        },
        filter: ['all', ['==', '$type', 'LineString'], ['==', ['get', 'filtradoEstado'], 'no_filtradas']],
    });


    const pointLayerSelected = (id) => ({
        id: `selectedfile-point-layer-${id}`,
        type: 'circle',
        paint: {
            'circle-radius': 5,
            'circle-color': '#198C19',
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
        },
        filter: ['==', '$type', 'Point'],
    });

    // --------------------------------------------------------------------------------
    //              Controles de capas (LayersIcon) - menú de estilos
    // --------------------------------------------------------------------------------
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const handleLayersClick = (event) => {
        try {
            setAnchorEl(event.currentTarget);
        } catch (error) {
            console.error('Error en handleLayersClick:', error);
        }
    };

    const handleLayersClose = () => {
        try {
            setAnchorEl(null);
        } catch (error) {
            console.error('Error en handleLayersClose:', error);
        }
    };

    const handleStyleSelect = (styleName) => {
        try {
            setSelectedStyle(styleName);
            setMapStyle(mapStyles[styleName]);
            handleLayersClose();
        } catch (error) {
            console.error('Error en handleStyleSelect:', error);
        }
    };

    // --------------------------------------------------------------------------------
    //  Prioridad de interacción: LineStrings siempre ganan sobre polígonos
    //  - Evita que los eventos/hover/click de polígonos se interpongan a las líneas.
    //  - Mantiene la UX: si el puntero cae sobre una línea, ignoramos el manejo de polígonos.
    // --------------------------------------------------------------------------------
    const getLineInteractionLayerIds = useCallback((map) => {
        if (!map) return [];

        const ids = [];
        const fixed = [
            'lines-layer-hitbox',
            'lines-layer',
            'lineas-no-filtradas-layer',
            'buffered-lines-layer',
            'trampling-lines-layer',
            'measurement-line-layer',
        ];

        fixed.forEach((id) => {
            try {
                if (map.getLayer(id)) ids.push(id);
            } catch (e) {
                // noop
            }
        });

        // Capas dinámicas (por ejemplo, selectedfile-*)
        try {
            const styleLayers = map.getStyle?.().layers || [];
            styleLayers.forEach((l) => {
                const id = l?.id;
                if (typeof id !== 'string') return;

                if (id.startsWith('selectedfile-line-layer-')) ids.push(id);
                if (id.startsWith('selectedfile-line-layer-hitbox-')) ids.push(id);
                if (id.startsWith('selectedfile-flight-layer-')) ids.push(id);

                // Herramientas de dibujo/corte (si existen)
                if (id === 'drawing-line-layer' || id === 'cutting-line-layer') ids.push(id);
            });
        } catch (e) {
            // noop
        }

        return Array.from(new Set(ids));
    }, []);

    const isPointerOnLine = useCallback(
        (map, point) => {
            try {
                if (!map || !point) return false;
                const layers = getLineInteractionLayerIds(map).filter((id) => {
                    try {
                        return !!map.getLayer(id);
                    } catch (e) {
                        return false;
                    }
                });
                if (layers.length === 0) return false;
                const hits = map.queryRenderedFeatures(point, { layers });
                return Array.isArray(hits) && hits.length > 0;
            } catch (e) {
                return false;
            }
        },
        [getLineInteractionLayerIds]
    );

    // --------------------------------------------------------------------------------
    //      Manejo de selección y popup en polígonos (polygons-data-layer)
    // --------------------------------------------------------------------------------
    const handlePolygonsDataClick = useCallback(
        (event) => {
            if (isEditing) return;

            try {
                const feature = event.features && event.features[0];
                if (!feature) return;

                const map = localMapRef.current?.getMap?.();
                if (!map) return;



                // Si el puntero/click cae sobre una línea, NO dispares el evento de polígonos
                if (event?.point && isPointerOnLine(map, event.point)) return;
// Fuente puede no existir temporalmente (cambios de estilo / datos vacíos)
                const src = map.getSource('polygons-data');
                if (!src) return;

                const properties = feature.properties || {};

                // Importante: feature-state requiere feature.id
                const rawFeatureId =
                    feature.id ??
                    properties.id ??
                    properties._id ??
                    properties.ID ??
                    properties.ID_LOTE ??
                    properties.LOTE_ID ??
                    properties.LOTEID ??
                    properties.loteId ??
                    properties.id_lote ??
                    properties.lote_id;

                if (rawFeatureId === undefined || rawFeatureId === null || String(rawFeatureId).trim() === '') {
                    console.warn('Polígono clickeado sin feature.id: no se puede aplicar feature-state.', feature);
                    return;
                }

                const featureId = String(rawFeatureId);

                if (selectedPolygonId === featureId) {
                    map.setFeatureState({ source: 'polygons-data', id: featureId }, { selected: false });
                    setSelectedPolygonId(null);
                    setSelectedPopup(null);
                } else {
                    if (selectedPolygonId !== null) {
                        map.setFeatureState({ source: 'polygons-data', id: selectedPolygonId }, { selected: false });
                    }

                    map.setFeatureState({ source: 'polygons-data', id: featureId }, { selected: true });
                    setSelectedPolygonId(featureId);

                    const content = Object.entries(properties).map(([key, value]) => (
                        <div key={key}>
                            <strong>{key.replace(/_/g, ' ')}:</strong> {value}
                        </div>
                    ));

                    setSelectedPopup({
                        lngLat: event.lngLat,
                        content: <div>{content}</div>,
                    });

                    // Mantener consistencia con la lógica de filtrado (activeLotes) usando el mismo extractor.
                    const loteId = getFeatureLoteId(properties);

                    // Si no podemos identificar un lote de forma confiable, NO activamos el filtro (evita que desaparezca todo).
                    if (loteId && loteId !== 'Sin ID' && `${loteId}`.trim() !== '') {
                        onSelectLote(loteId);
                    } else {
                        console.warn('Polígono clickeado sin un ID de lote reconocible; se muestra popup pero no se filtra.', properties);
                    }
                }
            } catch (error) {
                console.error('Error en handlePolygonsDataClick:', error);
            }
        },
        [selectedPolygonId, onSelectLote, isEditing, getFeatureLoteId]
    );

    useEffect(() => {
        try {
            if (!mapLoaded || !localMapRef.current || !localMapRef.current.getMap()) return;
            const map = localMapRef.current.getMap();

            const handleEnter = (e) => {
                try {
                    if (e?.point && isPointerOnLine(map, e.point)) return;
                    map.getCanvas().style.cursor = 'pointer';
                } catch (e) {
                    // noop
                }
            };

            const handleLeave = (e) => {
                try {
                    if (e?.point && isPointerOnLine(map, e.point)) return;
                    map.getCanvas().style.cursor = '';
                } catch (e) {
                    // noop
                }
            };

            map.on('click', 'polygons-data-layer', handlePolygonsDataClick);
            map.on('mouseenter', 'polygons-data-layer', handleEnter);
            map.on('mouseleave', 'polygons-data-layer', handleLeave);

            return () => {
                try {
                    map.off('click', 'polygons-data-layer', handlePolygonsDataClick);
                    map.off('mouseenter', 'polygons-data-layer', handleEnter);
                    map.off('mouseleave', 'polygons-data-layer', handleLeave);
                } catch (error) {
                    console.error('Error al remover eventos de polygons-data-layer:', error);
                }
            };
        } catch (error) {
            console.error('Error en useEffect de polygons-data-layer:', error);
            return undefined;
        }
    }, [mapLoaded, handlePolygonsDataClick, isPointerOnLine]);

    // --------------------------------------------------------------------------------
    //  Orden de capas (stacking)
    //   Requerimiento visual (de abajo hacia arriba):
    //    1) Polígonos de lotes / base (polygons-data / polygons / lotes-geojson)
    //    2) Ruta de vuelo (líneas blancas / no_filtradas)
    //    3) Líneas filtradas (moradas)
    //    4) Polígonos/buffers (buffered polygons, áreas, polígonos de archivos)
    //    5) Líneas de herramientas (trampling/measurement/drawing) si existen
    //    6) Intersecciones bufferizadas
    //    7) Imágenes (overlays) SIEMPRE arriba de todo
    //
    //  Se reaplica ante cambios de estilo (styledata) y al quedar el mapa idle para evitar
    //  que Mapbox reordene capas al cargar fuentes o al cambiar el estilo.
    // --------------------------------------------------------------------------------
    useEffect(() => {
        if (!mapLoaded || !localMapRef.current?.getMap?.()) return;
        const map = localMapRef.current.getMap();

        const ensureOrder = () => {
            try {
                const styleLayers = map.getStyle?.().layers || [];

                const exists = (id) => {
                    try {
                        return !!map.getLayer(id);
                    } catch (e) {
                        return false;
                    }
                };

                const collect = (predicate) => {
                    const ids = [];
                    styleLayers.forEach((l) => {
                        const id = l?.id;
                        if (typeof id !== 'string') return;
                        if (!predicate(id)) return;
                        try {
                            if (map.getLayer(id)) ids.push(id);
                        } catch (e) {
                            // noop
                        }
                    });
                    return ids;
                };

                // Capas dinámicas (selected files)
                const selectedFlightIds = collect((id) => id.startsWith('selectedfile-flight-layer-')).sort();
                const selectedLineIds = collect(
                    (id) => id.startsWith('selectedfile-line-layer-') && !id.startsWith('selectedfile-line-layer-hitbox-')
                ).sort();
                const selectedLineHitboxIds = collect((id) => id.startsWith('selectedfile-line-layer-hitbox-')).sort();

                const selectedPolygonNormalIds = collect(
                    (id) => id.startsWith('selectedfile-polygon-layer-') && !id.startsWith('selectedfile-polygon-layer-overlap-')
                ).sort();
                const selectedPolygonOverlapIds = collect((id) => id.startsWith('selectedfile-polygon-layer-overlap-')).sort();

                const selectedPointIds = collect((id) => id.startsWith('selectedfile-point-layer-')).sort();

                // Capas dinámicas de imágenes (cualquier raster cuyo source sea type="image")
                // Esto cubre tanto el overlay principal (imageUrl) como las imágenes provenientes de selectedFiles.
                const imageRasterLayerIds = (styleLayers || [])
                    .filter((l) => {
                        try {
                            if (!l || l.type !== 'raster') return false;
                            const srcId = l.source;
                            if (typeof srcId !== 'string' || !srcId) return false;
                            const src = map.getSource?.(srcId);
                            return src?.type === 'image';
                        } catch (e) {
                            return false;
                        }
                    })
                    .map((l) => l.id)
                    .filter((id) => typeof id === 'string')
                    .filter(exists);

                // 1) Polígonos base (lotes) - deben quedar abajo del stack
                const lotPolygonIds = [
                    'lotes-geojson-fill-layer',
                    'lotes-geojson-outline-layer',
                    'polygons-data-layer',
                    'polygons-layer',
                ].filter(exists);

                // 1b) VRA remoto: la maleza va justo sobre los lotes y la
                //     aplicación (franjas en polígonos) por encima de la maleza.
                //     Estas capas se montan antes que los lotes en el JSX, por lo
                //     que sin este paso quedaban tapadas por el relleno del lote.
                const varMalezaIds = [
                    'var-maleza-objetivo-layer',
                    'var-maleza-no-aplicada-layer',
                ].filter(exists);
                const varAplicacionIds = [
                    'var-aplicacion-poligonos-layer',
                    'var-aplicacion-poligonos-outline-layer',
                ].filter(exists);

                // 2) Ruta de vuelo (blancas / no_filtradas) - arriba de lotes, abajo de moradas/buffers
                const routeLayerIds = Array.from(
                    new Set(['lineas-no-filtradas-layer', ...selectedFlightIds])
                ).filter(exists);

                // 3) Líneas filtradas (moradas) - arriba de ruta, abajo de buffers/intersecciones
                //    Nota: el hitbox debe quedar arriba de su línea visual.
                const filteredLineIds = [
                    'lines-layer',
                    ...selectedLineIds,
                    'lines-layer-hitbox',
                    ...selectedLineHitboxIds,
                ].filter(exists);

                // 4) Buffers / polígonos de resultados - arriba de líneas filtradas
                //    Nota: overlaps arriba de normales.
                const bufferPolygonIds = [
                    'buffered-lines-layer',
                    'areas-superpuestas-layer',
                    'non-intersected-areas-layer',
                    ...selectedPolygonNormalIds,
                    ...selectedPolygonOverlapIds,
                ].filter(exists);

                // 5) Capas de herramientas (si existen) - suelen necesitar prioridad visual
                const toolLineIds = [
                    'trampling-lines-layer',
                    'measurement-line-layer',
                    'drawing-line-layer',
                    'cutting-line-layer',
                ].filter(exists);

                // Puntos (si existen): mantenerlos arriba de líneas/buffers, pero debajo de intersecciones.
                const pointLayerIds = ['points-layer', ...selectedPointIds].filter(exists);

                // 6) Intersecciones bufferizadas - siempre lo más arriba
                const intersectionIds = ['buffered-intersections-layer', 'buffered-intersections-outline-layer'].filter(exists);

                // 7) Imágenes (overlays) - SIEMPRE arriba
                const imageOverlayIds = [
                    // Overlay de tiles (si aplica)
                    'raster-tiles-layer',
                    // Overlay de imagen (si aplica)
                    ...imageRasterLayerIds,
                ].filter(exists);

                const moveInOrder = (ids) => {
                    ids.forEach((layerId) => {
                        try {
                            if (map.getLayer(layerId)) map.moveLayer(layerId);
                        } catch (e) {
                            // noop
                        }
                    });
                };

                // Mover en orden (de abajo hacia arriba): el último que se mueve queda más arriba.
                moveInOrder(lotPolygonIds);
                moveInOrder(varMalezaIds);
                moveInOrder(routeLayerIds);
                moveInOrder(filteredLineIds);
                moveInOrder(bufferPolygonIds);
                moveInOrder(varAplicacionIds);
                moveInOrder(toolLineIds);
                moveInOrder(pointLayerIds);
                moveInOrder(intersectionIds);
                moveInOrder(imageOverlayIds);
            } catch (e) {
                // noop
            }
        };

        // 1) Aplicar inmediatamente (cuando el DOM del mapa termina de montar)
        const t = setTimeout(ensureOrder, 0);

        // 2) Reaplicar cuando cambie el style (por ejemplo, al cambiar el estilo del mapa)
        const onStyleData = () => ensureOrder();
        map.on('styledata', onStyleData);
        map.on('idle', onStyleData);

        return () => {
            clearTimeout(t);
            try {
                map.off('styledata', onStyleData);
                map.off('idle', onStyleData);
            } catch (e) {
                // noop
            }
        };
    }, [mapLoaded]);


    // --------------------------------------------------------------------------------
    //   Helpers para hover/popup de lotes (lotes-geojson)
    // --------------------------------------------------------------------------------
    const pickProp = useCallback((properties, keys) => {
        if (!properties || typeof properties !== 'object') return null;

        const lower = {};
        Object.keys(properties).forEach((k) => {
            lower[k.toLowerCase()] = properties[k];
        });

        for (const k of keys) {
            const v = properties[k] ?? lower[(k || '').toLowerCase()];
            if (v !== undefined && v !== null && `${v}`.trim() !== '') return v;
        }
        return null;
    }, []);

    const buildHoverContent = useCallback(
        (properties = {}) => {
            const loteId = getFeatureLoteId(properties);

            const nFinca = pickProp(properties, [
                'N. FINCA',
                'N_FINCA',
                'NFINCA',
                'NOMBRE_FINCA',
                'NOMBREFINCA',
                'FINCA',
                'NOMBRE FINCA',
            ]);

            const cFinca = pickProp(properties, [
                'C. FINCA',
                'C_FINCA',
                'CFINCA',
                'CODIGO_FINCA',
                'CODIGOFINCA',
                'COD FINCA',
                'CODIGO FINCA',
            ]);

            const lote = pickProp(properties, [
                'LOTE',
                'N_LOTE',
                'N. LOTE',
                'NLOTE',
                'CODIGO_LOTE',
                'CODIGO LOTE',
            ]);

            const normalizeKey = (k) => String(k ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

            const excludedKeys = new Set(
                [
                    // ID de lote
                    'ID_LOTE',
                    'ID LOTE',
                    'IDLOTE',
                    'LOTE_ID',
                    'LOTE ID',
                    'ID',
                    '_ID',
                    'OBJECTID',
                    // N. finca
                    'N_FINCA',
                    'N. FINCA',
                    'NFINCA',
                    'NOMBREFINCA',
                    'NOMBRE_FINCA',
                    // C. finca
                    'C_FINCA',
                    'C. FINCA',
                    'CFINCA',
                    'CODIGO_FINCA',
                    'CODIGO FINCA',
                    'COD FINCA',
                    'CODIGOFINCA',
                    // Lote
                    'LOTE',
                    'N_LOTE',
                    'N. LOTE',
                    'NLOTE',
                    'CODIGO_LOTE',
                    'CODIGO LOTE',
                ].map(normalizeKey)
            );

            const extras = Object.entries(properties)
                .filter(
                    ([k, v]) =>
                        v !== undefined &&
                        v !== null &&
                        `${v}` !== '' &&
                        !excludedKeys.has(normalizeKey(k))
                )
                .sort(([a], [b]) => String(a).localeCompare(String(b)))
                .slice(0, 8);

            return (
                <div style={{ fontSize: '0.8rem', maxWidth: 260 }}>
                    <div>
                        <strong>ID Lote:</strong> {loteId}
                    </div>

                    {nFinca !== null && (
                        <div>
                            <strong>N. Finca:</strong> {nFinca}
                        </div>
                    )}
                    {cFinca !== null && (
                        <div>
                            <strong>C. Finca:</strong> {cFinca}
                        </div>
                    )}
                    {lote !== null && (
                        <div>
                            <strong>Lote:</strong> {lote}
                        </div>
                    )}

                    {extras.length > 0 && (
                        <div style={{ marginTop: 6, borderTop: '1px solid #ddd', paddingTop: 6 }}>
                            {extras.map(([k, v]) => (
                                <div key={k}>
                                    <strong>{String(k).replace(/_/g, ' ')}:</strong> {String(v)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        },
        [getFeatureLoteId, pickProp]
    );

    // --------------------------------------------------------------------------------
    //   Determinar si hay trampling lines (para ocultar/mostrar puntos)
    // --------------------------------------------------------------------------------
    const hasTramplingLines = useMemo(() => {
        if (!tramplingLines) return false;
        if (Array.isArray(tramplingLines) && tramplingLines.length > 0) return true;
        if (tramplingLines.url) return true;
        return false;
    }, [tramplingLines]);

    // --------------------------------------------------------------------------------
    //   useEffect para mostrar/ocultar capa de trampling lines (y fetch si es URL)
    // --------------------------------------------------------------------------------
    useEffect(() => {
        try {
            if (!mapLoaded || !localMapRef.current) return;

            const map = localMapRef.current.getMap();

            if (map.getSource('trampling-lines')) {
                if (map.getLayer('trampling-lines-layer')) {
                    map.removeLayer('trampling-lines-layer');
                }
                map.removeSource('trampling-lines');
            }

            if (!tramplingLines) return;
            if (Array.isArray(tramplingLines) && tramplingLines.length === 0) return;

            if (Array.isArray(tramplingLines) && tramplingLines.length > 0) {
                const geojsonData = {
                    type: 'FeatureCollection',
                    features: tramplingLines.map((line, idx) => ({
                        type: 'Feature',
                        id: line.id || `trampling-line-${idx}`,
                        geometry: line.geometry,
                        properties: { ...line.properties },
                    })),
                };

                map.addSource('trampling-lines', {
                    type: 'geojson',
                    data: geojsonData,
                });

                map.addLayer({
                    id: 'trampling-lines-layer',
                    type: 'line',
                    source: 'trampling-lines',
                    paint: {
                        'line-color': '#00FFFF',
                        'line-width': 3,
                    },
                });

                const bounds = new mapboxgl.LngLatBounds();
                geojsonData.features.forEach((feature) => {
                    const { type, coordinates } = feature.geometry;
                    if (type === 'LineString') {
                        coordinates.forEach((coord) => bounds.extend(coord));
                    } else if (type === 'MultiLineString') {
                        coordinates.forEach((singleLine) => singleLine.forEach((coord) => bounds.extend(coord)));
                    }
                });

                if (!bounds.isEmpty()) {
                    map.fitBounds(bounds, { padding: 20 });
                }

                return;
            }

            if (tramplingLines.url) {
                axios
                    .get(tramplingLines.url)
                    .then((response) => {
                        const data = response.data;
                        if (data && data.type === 'FeatureCollection' && Array.isArray(data.features) && data.features.length > 0) {
                            map.addSource('trampling-lines', {
                                type: 'geojson',
                                data,
                            });

                            map.addLayer({
                                id: 'trampling-lines-layer',
                                type: 'line',
                                source: 'trampling-lines',
                                paint: {
                                    'line-color': '#ff0000',
                                    'line-width': 3,
                                },
                            });

                            const bounds = new mapboxgl.LngLatBounds();
                            data.features.forEach((feature) => {
                                const { type, coordinates } = feature.geometry;
                                if (type === 'LineString') {
                                    coordinates.forEach((coord) => bounds.extend(coord));
                                } else if (type === 'MultiLineString') {
                                    coordinates.forEach((singleLine) => singleLine.forEach((coord) => bounds.extend(coord)));
                                }
                            });

                            if (!bounds.isEmpty()) {
                                map.fitBounds(bounds, { padding: 20 });
                            }
                        }
                    })
                    .catch((err) => {
                        console.error('Error al obtener trampling lines desde la URL:', err);
                    });
            }
        } catch (error) {
            console.error('Error en useEffect para tramplingLines:', error);
        }
    }, [mapLoaded, tramplingLines]);

    // --------------------------------------------------------------------------------
    //                Cerrar popup de polígono seleccionado
    // --------------------------------------------------------------------------------
    const handleClosePopup = () => {
        try {
            if (selectedPolygonId !== null && localMapRef.current) {
                const map = localMapRef.current.getMap();

                // En cambios de estilo o cuando el layer se desmonta, la fuente puede no existir.
                const src = map.getSource('polygons-data');
                if (src) {
                    try {
                        map.setFeatureState({ source: 'polygons-data', id: selectedPolygonId }, { selected: false });
                    } catch (e) {
                        // noop
                    }
                }

                setSelectedPolygonId(null);
            }
            setSelectedPopup(null);
        } catch (error) {
            console.error('Error en handleClosePopup:', error);
        }
    };

    // --------------------------------------------------------------------------------
    //                             NUEVA LÓGICA DE MEDICIÓN
    // --------------------------------------------------------------------------------
    const [measurementPoints, setMeasurementPoints] = useState([]);
    const [measurementDistance, setMeasurementDistance] = useState(0);
    const [measurementHoverCoord, setMeasurementHoverCoord] = useState(null);
    const [showMeasurementPopup, setShowMeasurementPopup] = useState(false);

    useEffect(() => {
        if (activeTool !== 'measurement') {
            setMeasurementPoints([]);
            setMeasurementDistance(0);
            setMeasurementHoverCoord(null);
            setShowMeasurementPopup(false);
        }
    }, [activeTool]);

    useEffect(() => {
        if (!mapLoaded || !localMapRef.current || activeTool !== 'measurement') return;
        const map = localMapRef.current.getMap();

        const handleMapClick = (e) => {
            if (measurementPoints.length >= 2) {
                setMeasurementPoints([e.lngLat]);
                setMeasurementDistance(0);
            } else {
                setMeasurementPoints([...measurementPoints, e.lngLat]);
            }
        };

        map.on('click', handleMapClick);

        return () => {
            map.off('click', handleMapClick);
        };
    }, [mapLoaded, activeTool, measurementPoints]);

    useEffect(() => {
        if (!mapLoaded || !localMapRef.current || activeTool !== 'measurement') return;
        const map = localMapRef.current.getMap();

        if (measurementPoints.length === 1) {
            const handleMouseMove = (e) => {
                setMeasurementHoverCoord(e.lngLat);
                if (measurementPoints[0]) {
                    const from = turf.point([measurementPoints[0].lng, measurementPoints[0].lat]);
                    const to = turf.point([e.lngLat.lng, e.lngLat.lat]);
                    const distanceInMeters = turf.distance(from, to, { units: 'meters' });
                    setMeasurementDistance(distanceInMeters);
                    setShowMeasurementPopup(true);
                }
            };

            map.on('mousemove', handleMouseMove);

            return () => {
                map.off('mousemove', handleMouseMove);
            };
        }

        return undefined;
    }, [mapLoaded, activeTool, measurementPoints]);

    const measurementLineData = useMemo(() => {
        if (measurementPoints.length === 0) return null;
        let coords = measurementPoints.map((p) => [p.lng, p.lat]);
        if (measurementPoints.length === 1 && measurementHoverCoord) {
            coords.push([measurementHoverCoord.lng, measurementHoverCoord.lat]);
        }
        if (coords.length < 2) return null;

        return {
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: coords,
                    },
                    properties: {},
                },
            ],
        };
    }, [measurementPoints, measurementHoverCoord]);

    const distanceString = useMemo(() => {
        if (measurementDistance < 1000) {
            return `${measurementDistance.toFixed(2)} m`;
        }
        const km = measurementDistance / 1000;
        return `${km.toFixed(2)} km`;
    }, [measurementDistance]);

    // --------------------------------------------------------------------------------
    //  Determinamos cuál set de polígonos se dibuja en la capa principal
    // --------------------------------------------------------------------------------
    const baseFinalPolygonsData = useMemo(() => {
        if (safePolygonsPlanning.features.length > 0) {
            return safePolygonsPlanning;
        }
        if (polygonDataFeatures.features.length > 0) {
            return polygonDataFeatures;
        }
        if (polygonDataMemo.features.length > 0) {
            return polygonDataMemo;
        }
        return { type: 'FeatureCollection', features: [] };
    }, [safePolygonsPlanning, polygonDataFeatures, polygonDataMemo]);

    const finalPolygonsData = useMemo(() => {
        const byActive = filterFCByActiveLotes(baseFinalPolygonsData);

        if (clipPolygonsFC?.type === 'FeatureCollection' && (clipPolygonsFC.features?.length || 0) > 0) {
            return clipPolygonsFC;
        }

        return byActive;
    }, [baseFinalPolygonsData, filterFCByActiveLotes, clipPolygonsFC]);

    // Asegura feature.id para que feature-state (selección/hover) funcione sin errores.
    const finalPolygonsDataWithIds = useMemo(() => {
        return normalizeFCWithIds(finalPolygonsData, 'polygons-data');
    }, [finalPolygonsData, normalizeFCWithIds]);

    const polygonsForClipping = useMemo(() => {
        if (clipPolygonsFC?.type === 'FeatureCollection' && (clipPolygonsFC.features?.length || 0) > 0) {
            return clipPolygonsFC;
        }
        return finalPolygonsData;
    }, [clipPolygonsFC, finalPolygonsData]);

    const spatialFilterEnabled = useMemo(() => {
        const hasActive = (activeLotes?.length || 0) > 0;
        const hasClipPolys = (clipPolygonsFC?.features?.length || 0) > 0;

        const hasSelection = hasActive || hasClipPolys;
        const hasPolygonsToClip = (polygonsForClipping?.features?.length || 0) > 0;

        return hasSelection && hasPolygonsToClip;
    }, [activeLotes, clipPolygonsFC, polygonsForClipping]);

    const clipFCSpatial = useCallback(
        (fc) => {
            if (!fc || !Array.isArray(fc.features)) {
                return { type: 'FeatureCollection', features: [] };
            }
            if (isTasaVariableMap) return fc;
            if (!spatialFilterEnabled) return fc;

            const clipped = clipFeaturesToPolygons(fc.features, polygonsForClipping);

            if (clipped?.type === 'FeatureCollection') return clipped;
            if (Array.isArray(clipped)) return { type: 'FeatureCollection', features: clipped };

            return { type: 'FeatureCollection', features: [] };
        },
        [spatialFilterEnabled, polygonsForClipping, isTasaVariableMap]
    );

    // Versiones RECORTADAS (clip) de TODAS las capas
    const pointDataFiltered = useMemo(() => clipFCSpatial(pointData), [pointData, clipFCSpatial]);
    const lineDataFiltered = useMemo(() => clipFCSpatial(lineData), [lineData, clipFCSpatial]);
    const lineasNoFiltradasDataFiltered = useMemo(() => clipFCSpatial(lineasNoFiltradasData), [lineasNoFiltradasData, clipFCSpatial]);
    const bufferedLinesDataFiltered = useMemo(() => clipFCSpatial(bufferedLinesData), [bufferedLinesData, clipFCSpatial]);

    useEffect(() => {
        try {
            if (typeof onClippedBufferChange === 'function') {
                onClippedBufferChange(bufferedLinesDataFiltered);
            }
            if (typeof onClipPolygonsChange === 'function') {
                onClipPolygonsChange(polygonsForClipping);
            }
        } catch (e) {
            console.error('Error reportando buffer/polígonos clipeados:', e);
        }
    }, [bufferedLinesDataFiltered, polygonsForClipping, onClippedBufferChange, onClipPolygonsChange]);

    const bufferedIntersectionsDataFiltered = useMemo(
        () => clipFCSpatial(bufferedIntersectionsData),
        [bufferedIntersectionsData, clipFCSpatial]
    );

    const areasSuperpuestasDataFiltered = useMemo(
        () => clipFCSpatial(areasSuperpuestasData),
        [areasSuperpuestasData, clipFCSpatial]
    );

    const nonIntersectedAreasDataFiltered = useMemo(
        () => clipFCSpatial(nonIntersectedAreasData),
        [nonIntersectedAreasData, clipFCSpatial]
    );

    const polygonDataMemoFiltered = useMemo(() => clipFCSpatial(polygonDataMemo), [polygonDataMemo, clipFCSpatial]);

    const selectedFilesDataFiltered = useMemo(() => {
        if (!spatialFilterEnabled) return selectedFilesData;

        return (selectedFilesData || []).map((fd) => {
            if (fd?.type === 'geojson' && fd?.data) {
                return { ...fd, data: clipFCSpatial(fd.data) };
            }
            return fd;
        });
    }, [selectedFilesData, spatialFilterEnabled, clipFCSpatial]);

    const interactiveLayers = useMemo(() => {
        const layers = [];
        selectedFilesDataFiltered.forEach((fileData, index) => {
            if (fileData.type === 'geojson') {
                layers.push(`selectedfile-polygon-layer-normal-${index}`);
                layers.push(`selectedfile-polygon-layer-overlap-${index}`);
                layers.push(`selectedfile-line-layer-${index}`);
                layers.push(`selectedfile-point-layer-${index}`);
            } else if (fileData.type === 'image' && fileData.coordinates) {
                layers.push(`${fileData.id}-layer`);
            }
        });
        return layers;
    }, [selectedFilesDataFiltered]);

    // --------------------------------------------------------------------------------
    //  useEffect para hover sobre lotes-geojson (CORREGIDO: bloque válido y sin try/catch suelto)
    // --------------------------------------------------------------------------------
    useEffect(() => {
        try {
            if (!mapLoaded || !localMapRef.current || !localMapRef.current.getMap()) return;
            if (!lotesGeojsonData || !Array.isArray(lotesGeojsonData.features) || lotesGeojsonData.features.length === 0) {
                return;
            }

            const map = localMapRef.current.getMap();
            const sourceId = 'lotes-geojson';
            const layerId = 'lotes-geojson-fill-layer';

            let isBound = false;
            let cancelled = false;

            const handleEnter = (e) => {
                try {
                    if (e?.point && isPointerOnLine(map, e.point)) return;
                    map.getCanvas().style.cursor = 'pointer';
                } catch (e) {
                    // noop
                }
            };

            const handleMove = (e) => {
                try {
                    // Si el puntero está sobre una línea, NO mostrar hover/popup de polígonos
                    if (e?.point && isPointerOnLine(map, e.point)) {
                        try {
                            if (hoveredLoteFeatureIdRef.current !== null) {
                                map.setFeatureState({ source: sourceId, id: hoveredLoteFeatureIdRef.current }, { hover: false });
                            }
                        } catch (err) {
                            // noop
                        }

                        hoveredLoteFeatureIdRef.current = null;
                        lastHoveredLoteIdRef.current = null;
                        setHoverLotePopup(null);
                        onLeaveLote();
                        return;
                    }
                    const feature = e.features && e.features[0];
                    if (!feature) return;

                    const featureId = feature.id;
                    if (featureId === undefined || featureId === null) return;

                    if (hoveredLoteFeatureIdRef.current !== null && hoveredLoteFeatureIdRef.current !== featureId) {
                        try {
                            map.setFeatureState({ source: sourceId, id: hoveredLoteFeatureIdRef.current }, { hover: false });
                        } catch (err) {
                            // noop
                        }
                    }

                    hoveredLoteFeatureIdRef.current = featureId;

                    try {
                        map.setFeatureState({ source: sourceId, id: featureId }, { hover: true });
                    } catch (err) {
                        // noop
                    }

                    try {
                        map.getCanvas().style.cursor = 'pointer';
                    } catch (e2) {
                        // noop
                    }

                    const properties = feature.properties || {};
                    const loteId = getFeatureLoteId(properties);

                    if (lastHoveredLoteIdRef.current !== loteId) {
                        lastHoveredLoteIdRef.current = loteId;
                        onHoverLote(loteId);
                    }

                    setHoverLotePopup({
                        lngLat: e.lngLat,
                        content: buildHoverContent(properties),
                    });
                } catch (err) {
                    console.error('Error en hover de lotes-geojson:', err);
                }
            };

            const handleLeave = (e) => {
                try {
                    try {
                        map.getCanvas().style.cursor = '';
                    } catch (e) {
                        // noop
                    }

                    if (hoveredLoteFeatureIdRef.current !== null) {
                        try {
                            map.setFeatureState({ source: sourceId, id: hoveredLoteFeatureIdRef.current }, { hover: false });
                        } catch (err) {
                            // noop
                        }
                        hoveredLoteFeatureIdRef.current = null;
                    }

                    lastHoveredLoteIdRef.current = null;
                    setHoverLotePopup(null);
                    onLeaveLote();
                } catch (err) {
                    console.error('Error en mouseleave de lotes-geojson:', err);
                }
            };

            const bind = () => {
                if (cancelled) return;

                if (!map.getLayer(layerId)) {
                    requestAnimationFrame(bind);
                    return;
                }

                if (isBound) return;

                map.on('mouseenter', layerId, handleEnter);
                map.on('mousemove', layerId, handleMove);
                map.on('mouseleave', layerId, handleLeave);

                isBound = true;
            };

            bind();

            return () => {
                cancelled = true;

                try {
                    if (isBound) {
                        map.off('mouseenter', layerId, handleEnter);
                        map.off('mousemove', layerId, handleMove);
                        map.off('mouseleave', layerId, handleLeave);
                    }
                } catch (err) {
                    console.error('Error al remover eventos hover de lotes-geojson:', err);
                }

                try {
                    if (hoveredLoteFeatureIdRef.current !== null) {
                        map.setFeatureState({ source: sourceId, id: hoveredLoteFeatureIdRef.current }, { hover: false });
                    }
                } catch (e) {
                    // noop
                }

                hoveredLoteFeatureIdRef.current = null;
                lastHoveredLoteIdRef.current = null;
                setHoverLotePopup(null);
            };
        } catch (error) {
            console.error('Error en useEffect hover lotes-geojson:', error);
            return undefined;
        }
    }, [mapLoaded, lotesGeojsonData, getFeatureLoteId, onHoverLote, onLeaveLote, buildHoverContent, isPointerOnLine]);

    const getHarvestTitle = (groupKey) => {
        if (!groupKey) return '';
        const sinPrefijo = groupKey.replace(/^agrupacion_/, '');
        const palabras = sinPrefijo.split('_');
        const capitalizadas = palabras.map((p) => p.charAt(0).toUpperCase() + p.slice(1));
        return capitalizadas.join(' ');
    };

    const harvestTitle = useMemo(() => getHarvestTitle(selectedHarvestGrouping), [selectedHarvestGrouping]);

    const handleLabelClick = useCallback(
        (text, color) => {
            if (typeof setLabelsAndColors === 'function') {
                setLabelsAndColors({ text, color });
            }
        },
        [setLabelsAndColors]
    );

    useEffect(() => {
        if (mapLoaded && localMapRef.current) {
            const map = localMapRef.current.getMap();
            const currentProjection = 'EPSG:4326';

            if (!northWestCoords || !southEastCoords) {
                const bounds = map.getBounds();
                const nw = bounds.getNorthWest();
                const se = bounds.getSouthEast();
                const newNW = [nw.lng, nw.lat];
                const newSE = [se.lng, se.lat];
                setNorthWestCoords(newNW);
                setSouthEastCoords(newSE);
                setCoordinateSystem(currentProjection);
            }

            const updateBounds = () => {
                if (northWestCoords && southEastCoords) return;
                const bounds = map.getBounds();
                const nw = bounds.getNorthWest();
                const se = bounds.getSouthEast();
                const newNW = [nw.lng, nw.lat];
                const newSE = [se.lng, se.lat];
                setNorthWestCoords(newNW);
                setSouthEastCoords(newSE);
                setCoordinateSystem(currentProjection);
            };

            map.on('moveend', updateBounds);

            return () => {
                map.off('moveend', updateBounds);
            };
        }
        return undefined;
    }, [mapLoaded, northWestCoords, southEastCoords, setNorthWestCoords, setSouthEastCoords, setCoordinateSystem]);

    return (
        <Box position="relative">
            {selectedHarvestGrouping && (
                <BarIndicator
                    filterType="harvestGrouping"
                    onLabelClick={handleLabelClick}
                    setLabelsAndColors={setLabelsAndColors}
                    harvestLabels={harvestLegendLabels}
                    harvestTitle={harvestTitle}
                />
            )}

            <MapDialog
                isOpen={!!isFilterDialogOpen}
                onClose={() => {
                    if (typeof closeFilterDialog === 'function') closeFilterDialog();
                }}
                harvestGroupings={harvestGroupings}
                selectedHarvestGrouping={selectedHarvestGrouping}
                onSelectHarvestGrouping={setSelectedHarvestGrouping}
                setTipoMapeo={setTipoMapeo}
            />

            <Map
                initialViewState={{
                    longitude: center[1],
                    latitude: center[0],
                    zoom: zoom,
                }}
                mapStyle={mapStyle}
                mapboxAccessToken="pk.eyJ1IjoiamRtYW4iLCJhIjoiY20xNGpkN2d0MHlsejJ4cTA1Z3lwdGNhMCJ9.O5UY8q02UPPoCCMrobQ0sA"
                style={{ height: '65vh', width: '100%' }}
                ref={localMapRef}
                projection={isTasaVariableMap ? "mercator" : "globe"}
                preserveDrawingBuffer={!isTasaVariableMap}
                fadeDuration={isTasaVariableMap ? 0 : 300}
                crossSourceCollisions={!isTasaVariableMap}
                renderWorldCopies={!isTasaVariableMap}
                onLoad={handleMapLoad}
                onMoveEnd={handleTasaVariableMoveEnd}
                interactiveLayerIds={['polygons-data-layer', ...interactiveLayers]}
            >
                <NavigationControl position="top-right" />

                {isTiled && (
                    <Source id="raster-tiles-overlay" type="raster" tiles={[imageUrl]} tileSize={256}>
                        <Layer
                            id="raster-tiles-layer"
                            type="raster"
                            paint={{
                                'raster-opacity': 1,
                                'raster-resampling': 'linear',
                            }}
                        />
                    </Source>
                )}

                {!isTiled && imageUrl && northWestCoords && southEastCoords && (
                    <Source
                        id="image-overlay"
                        type="image"
                        url={imageUrl}
                        coordinates={[
                            [northWestCoords[0], northWestCoords[1]],
                            [southEastCoords[0], northWestCoords[1]],
                            [southEastCoords[0], southEastCoords[1]],
                            [northWestCoords[0], southEastCoords[1]],
                        ]}
                    >
                        <Layer
                            id="image-overlay-layer"
                            type="raster"
                            paint={{
                                'raster-opacity': 1,
                                'raster-resampling': 'linear',
                            }}
                        />
                    </Source>
                )}

                {/*
                    VAR optimizado: Mapbox recibe URLs GeoJSON directamente y
                    procesa/tilea las geometrías en sus propios workers. El
                    nivel general usa capas overview livianas y el zoom cercano
                    activa automáticamente las geometrías exactas.
                */}
                {hasRemoteTasaVariableLayers && effectiveTasaVariableRemoteLayers.malezaObjetivo && (
                    <Source
                        id="var-maleza-objetivo-remote"
                        type="geojson"
                        data={effectiveTasaVariableRemoteLayers.malezaObjetivo}
                        tolerance={showDetailedTasaVariableLayers ? 0 : 0.5}
                        buffer={showDetailedTasaVariableLayers ? 64 : 16}
                        maxzoom={showDetailedTasaVariableLayers ? 18 : 14}
                    >
                        <Layer
                            id="var-maleza-objetivo-layer"
                            type="fill"
                            paint={{
                                'fill-color': '#00ACC1',
                                'fill-opacity': 0.24,
                                'fill-outline-color': '#006064',
                                'fill-antialias': false,
                            }}
                        />
                    </Source>
                )}

                {hasRemoteTasaVariableLayers && effectiveTasaVariableRemoteLayers.malezaNoAplicada && (
                    <Source
                        id="var-maleza-no-aplicada-remote"
                        type="geojson"
                        data={effectiveTasaVariableRemoteLayers.malezaNoAplicada}
                        tolerance={showDetailedTasaVariableLayers ? 0 : 0.5}
                        buffer={showDetailedTasaVariableLayers ? 64 : 16}
                        maxzoom={showDetailedTasaVariableLayers ? 18 : 14}
                    >
                        <Layer
                            id="var-maleza-no-aplicada-layer"
                            type="fill"
                            paint={{
                                'fill-color': '#7E57C2',
                                'fill-opacity': 0.62,
                                'fill-outline-color': '#4527A0',
                                'fill-antialias': false,
                            }}
                        />
                    </Source>
                )}

                {hasRemoteTasaVariableLayers && isPolygonApplicationRemote && (
                    <Source
                        id="var-aplicacion-poligonos-remote"
                        type="geojson"
                        data={effectiveTasaVariableRemoteLayers.aplicacionPoligonos || effectiveTasaVariableRemoteLayers.lineasAplicacion}
                        tolerance={showDetailedTasaVariableLayers ? 0 : 0.5}
                        buffer={showDetailedTasaVariableLayers ? 64 : 16}
                        maxzoom={showDetailedTasaVariableLayers ? 18 : 14}
                    >
                        <Layer
                            id="var-aplicacion-poligonos-layer"
                            type="fill"
                            paint={{
                                'fill-color': ['coalesce', ['get', 'color'], '#198C19'],
                                'fill-opacity': 0.62,
                                'fill-antialias': false,
                            }}
                        />
                        <Layer
                            id="var-aplicacion-poligonos-outline-layer"
                            type="line"
                            paint={{
                                'line-color': ['coalesce', ['get', 'color'], '#198C19'],
                                'line-width': [
                                    'interpolate',
                                    ['linear'],
                                    ['zoom'],
                                    12, 0.2,
                                    16, 0.6,
                                    18, 1.0,
                                ],
                                'line-opacity': 0.9,
                            }}
                        />
                    </Source>
                )}

                {hasRemoteTasaVariableLayers && !isPolygonApplicationRemote && effectiveTasaVariableRemoteLayers.lineasAplicacion && (
                    <Source
                        id="lines"
                        type="geojson"
                        data={effectiveTasaVariableRemoteLayers.lineasAplicacion}
                        tolerance={showDetailedTasaVariableLayers ? 0 : 0.5}
                        buffer={showDetailedTasaVariableLayers ? 64 : 16}
                        maxzoom={showDetailedTasaVariableLayers ? 18 : 14}
                        generateId={true}
                    >
                        <Layer
                            id="lines-layer"
                            type="line"
                            paint={{
                                'line-color': [
                                    'case',
                                    ['==', ['feature-state', 'hover'], true],
                                    '#E0FFFF',
                                    ['coalesce', ['get', 'color'], '#9b00a9'],
                                ],
                                'line-width': [
                                    'interpolate',
                                    ['linear'],
                                    ['zoom'],
                                    12, 0.65,
                                    16, 1.15,
                                    18, 1.65,
                                ],
                            }}
                        />
                    </Source>
                )}

                {!hasRemoteTasaVariableLayers && !hasTramplingLines && pointDataFiltered && pointDataFiltered.features.length > 0 && (
                    <Source id="points" type="geojson" data={pointDataFiltered}>
                        <Layer
                            id="points-layer"
                            type="circle"
                            paint={{
                                'circle-radius': 2,
                                'circle-color': ['get', 'color'],
                            }}
                        />
                    </Source>
                )}

                {!hasRemoteTasaVariableLayers && lineasNoFiltradasDataFiltered && lineasNoFiltradasDataFiltered.features.length > 0 && (
                    <Source id="lineas-no-filtradas" type="geojson" data={lineasNoFiltradasDataFiltered}>
                        <Layer
                            id="lineas-no-filtradas-layer"
                            type="line"
                            beforeId={lineDataFiltered && lineDataFiltered.features.length > 0 ? 'lines-layer' : undefined}
                            paint={{
                                'line-color': ['get', 'color'],
                                'line-width': [
                                    'interpolate',
                                    ['linear'],
                                    ['zoom'],
                                    12, 0.65,
                                    16, 1.15,
                                    18, 1.65,
                                ],
                            }}
                        />
                    </Source>
                )}

                {!hasRemoteTasaVariableLayers && lineDataFiltered && lineDataFiltered.features.length > 0 && (
                    <Source id="lines" type="geojson" data={lineDataFiltered}>
                        <Layer
                            id="lines-layer"
                            type="line"
                            paint={{
                                'line-color': [
                                    'case',
                                    ['==', ['feature-state', 'hover'], true],
                                    '#E0FFFF',
                                    ['get', 'color'],
                                ],
                                'line-width': isTasaVariableMap ? 3 : 2,
                            }}
                        />
                        <Layer
                            id="lines-layer-hitbox"
                            type="line"
                            paint={{
                                // Hitbox transparente para facilitar hover/click sin cambiar lo visual
                                'line-color': '#000000',
                                'line-width': 14,
                                'line-opacity': 0.01,
                            }}
                        />
                    </Source>
                )}

                {lotesGeojsonData && lotesGeojsonData.features.length > 0 && (
                    <Source id="lotes-geojson" type="geojson" data={lotesGeojsonData}>
                        <Layer
                            id="lotes-geojson-fill-layer"
                            type="fill"
                            paint={{
                                'fill-color': [
                                    'case',
                                    ['==', ['feature-state', 'hover'], true],
                                    '#00FFFF',
                                    '#000000',
                                ],
                                'fill-opacity': [
                                    'case',
                                    ['==', ['feature-state', 'hover'], true],
                                    0.25,
                                    0.001,
                                ],
                            }}
                        />
                        <Layer
                            id="lotes-geojson-outline-layer"
                            type="line"
                            paint={{
                                'line-color': [
                                    'case',
                                    ['==', ['feature-state', 'hover'], true],
                                    '#00FFFF',
                                    '#000000',
                                ],
                                'line-width': [
                                    'case',
                                    ['==', ['feature-state', 'hover'], true],
                                    3,
                                    1,
                                ],
                            }}
                        />
                    </Source>
                )}

                {/*
                    Importante: Mantener siempre la fuente/capa en el style evita errores al cerrar popup
                    y permite usar feature-state sin que falte la source.
                */}
                <Source id="polygons-data" type="geojson" data={finalPolygonsDataWithIds} generateId={true}>
                    <Layer
                        id="polygons-data-layer"
                        type="fill"
                        paint={{
                            'fill-color': selectedHarvestGrouping
                                ? ['get', selectedHarvestGrouping.replace('agrupacion_', 'color_')]
                                : 'yellow',
                            'fill-opacity': [
                                'case',
                                ['boolean', ['feature-state', 'selected'], false],
                                0.95,
                                0.8,
                            ],
                            'fill-outline-color': [
                                'case',
                                ['boolean', ['feature-state', 'selected'], false],
                                '#00FFFF',
                                '#000',
                            ],
                        }}
                    />
                </Source>

                {!hasRemoteTasaVariableLayers && polygonDataMemoFiltered && polygonDataMemoFiltered.features.length > 0 && (
                    <Source id="polygons" type="geojson" data={polygonDataMemoFiltered}>
                        <Layer
                            id="polygons-layer"
                            type="fill"
                            paint={{
                                'fill-color': [
                                    'case',
                                    ['boolean', ['get', 'active'], false],
                                    'red',
                                    ['has', 'color'],
                                    ['get', 'color'],
                                    '#198C19',
                                ],
                                'fill-opacity': [
                                    'coalesce',
                                    ['get', 'opacity'],
                                    1,
                                ],
                                'fill-outline-color': '#000',
                            }}
                        />
                    </Source>
                )}

                {bufferedLinesDataFiltered && bufferedLinesDataFiltered.features.length > 0 && (
                    <Source id="buffered-lines" type="geojson" data={bufferedLinesDataFiltered}>
                        <Layer
                            id="buffered-lines-layer"
                            type="fill"
                            paint={{
                                'fill-color': 'green',
                                'fill-opacity': 0.8,
                            }}
                        />
                    </Source>
                )}

                {shouldShowIntersections &&
                    bufferedIntersectionsDataFiltered &&
                    bufferedIntersectionsDataFiltered.features.length > 0 && (
                        <Source id="buffered-intersections" type="geojson" data={bufferedIntersectionsDataFiltered}>
                            <Layer
                                id="buffered-intersections-layer"
                                type="fill"
                                paint={{
                                    'fill-color': 'red',
                                    'fill-opacity': 1,
                                }}
                            />
                            <Layer
                                id="buffered-intersections-outline-layer"
                                type="line"
                                paint={{
                                    'line-color': 'red',
                                    'line-width': [
                                    'interpolate',
                                    ['linear'],
                                    ['zoom'],
                                    12, 0.65,
                                    16, 1.15,
                                    18, 1.65,
                                ],
                                    'line-opacity': 1,
                                }}
                            />
                        </Source>
                    )}

                {shouldShowIntersections && areasSuperpuestasDataFiltered && areasSuperpuestasDataFiltered.features.length > 0 && (
                    <Source id="areas-superpuestas" type="geojson" data={areasSuperpuestasDataFiltered}>
                        <Layer
                            id="areas-superpuestas-layer"
                            type="fill"
                            paint={{
                                'fill-color': 'red',
                                'fill-opacity': 1,
                                'fill-outline-color': 'red',
                            }}
                        />
                    </Source>
                )}

                {nonIntersectedAreasDataFiltered && nonIntersectedAreasDataFiltered.features.length > 0 && (
                    <Source id="non-intersected-areas" type="geojson" data={nonIntersectedAreasDataFiltered}>
                        <Layer
                            id="non-intersected-areas-layer"
                            type="fill"
                            paint={{
                                'fill-color': 'yellow',
                                'fill-opacity': 1,
                            }}
                        />
                    </Source>
                )}

                {selectedFilesDataFiltered &&
                    selectedFilesDataFiltered.map((fileData, index) => {
                        try {
                            if (fileData.type === 'geojson' && fileData.data) {
                                return (
                                    <Source key={fileData.id} id={fileData.id} type="geojson" data={fileData.data}>
                                        <Layer {...flightRouteLineLayerSelected(index)} />
                                        <Layer {...polygonLayerSelectedNormal(index)} />
                                        <Layer {...polygonLayerSelectedOverlap(index)} />
                                        <Layer {...lineLayerSelected(index)} />
                                        <Layer {...pointLayerSelected(index)} />
                                    </Source>
                                );
                            }

                            if (fileData.type === 'image' && fileData.coordinates) {
                                return (
                                    <Source
                                        key={fileData.id}
                                        id={fileData.id}
                                        type="image"
                                        url={fileData.imageUrl}
                                        coordinates={fileData.coordinates}
                                    >
                                        <Layer id={`${fileData.id}-layer`} type="raster" paint={{ 'raster-opacity': 1 }} />
                                    </Source>
                                );
                            }

                            return null;
                        } catch (renderErr) {
                            console.error(`Error al renderizar selectedFilesData en index ${index}:`, renderErr);
                            return null;
                        }
                    })}

                {measurementLineData && (
                    <Source id="measurement-line" type="geojson" data={measurementLineData}>
                        <Layer
                            id="measurement-line-layer"
                            type="line"
                            paint={{
                                'line-color': '#FF0000',
                                'line-width': [
                                    'interpolate',
                                    ['linear'],
                                    ['zoom'],
                                    12, 0.65,
                                    16, 1.15,
                                    18, 1.65,
                                ],
                                'line-dasharray': [2, 1],
                            }}
                        />
                    </Source>
                )}

                {hoverLotePopup && hoverLotePopup.lngLat && hoverLotePopup.content && (
                    <Popup
                        longitude={hoverLotePopup.lngLat.lng}
                        latitude={hoverLotePopup.lngLat.lat}
                        closeButton={false}
                        closeOnClick={false}
                        className="lote-hover-popup"
                    >
                        {hoverLotePopup.content}
                    </Popup>
                )}

                {selectedPopup && selectedPopup.lngLat && selectedPopup.content && (
                    <Popup
                        longitude={selectedPopup.lngLat.lng}
                        latitude={selectedPopup.lngLat.lat}
                        onClose={handleClosePopup}
                        closeOnClick={false}
                    >
                        {selectedPopup.content}
                    </Popup>
                )}

                {popupInfo && popupInfo.lngLat && popupInfo.content && (
                    <Popup
                        longitude={popupInfo.lngLat.lng}
                        latitude={popupInfo.lngLat.lat}
                        onClose={() => setPopupInfo(null)}
                        closeOnClick={false}
                    >
                        {popupInfo.content}
                    </Popup>
                )}

                {selectedFilesPopup && selectedFilesPopup.lngLat && selectedFilesPopup.content && (
                    <Popup
                        longitude={selectedFilesPopup.lngLat.lng}
                        latitude={selectedFilesPopup.lngLat.lat}
                        onClose={() => setSelectedFilesPopup(null)}
                        closeOnClick={false}
                    >
                        {selectedFilesPopup.content}
                    </Popup>
                )}

                {showMeasurementPopup && measurementHoverCoord && (
                    <Popup
                        longitude={measurementHoverCoord.lng}
                        latitude={measurementHoverCoord.lat}
                        closeButton={false}
                        closeOnClick={false}
                    >
                        <div style={{ fontSize: '0.8rem' }}>
                            <strong>Distancia:</strong> {distanceString}
                        </div>
                    </Popup>
                )}
            </Map>

            <Box position="absolute" top={10} left={10} zIndex={1000}>
                <IconButton
                    sx={{
                        backgroundColor: 'white',
                        color: 'black',
                        '&:hover': { backgroundColor: 'lightgray' },
                    }}
                    onClick={handleLayersClick}
                >
                    <LayersIcon />
                </IconButton>

                <Menu
                    id="layer-menu"
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={handleLayersClose}
                    PaperProps={{
                        style: {
                            maxHeight: 200,
                            width: 'auto',
                            padding: 0,
                        },
                    }}
                >
                    {Object.keys(mapStyles).map((styleName) => (
                        <MenuItem
                            key={styleName}
                            onClick={() => handleStyleSelect(styleName)}
                            sx={{ fontSize: '0.85rem' }}
                        >
                            {styleName}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>

            <Box position="absolute" top={10} right={10} zIndex={1000}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <StyledIconButton onClick={handleCenterToPolygonsData}>
                        <HomeIcon fontSize="small" />
                    </StyledIconButton>
                    <StyledIconButton onClick={handleCenterToAnalysis}>
                        <MyLocationIcon fontSize="small" />
                    </StyledIconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default CommonMap;
